///<reference path='../../../../../Vssf/WebPlatform/Features/CodeEditor/Scripts/CodeEditor/Monaco.Editor.d.ts' />
/// <amd-dependency path="Mention/Scripts/TFS.Mention.WorkItems"/>

import TFS_Extension = require("BuiltInExtensions/Scripts/TFS.Extension");
import TFSCodeEditorResources = require("BuiltInExtensions/Scripts/Resources/TFS.Resources.CodeEditor");

import { ContributionLoader, getResourceService } from "BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor.ContributionLoader";
import { RichContentTooltip } from "VSS/Controls/PopupContent";
import { getLWPService } from "VSS/LWP";
import { getScenarioManager } from "VSS/Performance";
import { IContextMenuItem } from "./TFS.Extensions.CodeEditor.Contributions";
import Utils_Array = require("VSS/Utils/Array");
import Utils_Core = require("VSS/Utils/Core");
import Utils_String = require("VSS/Utils/String");
import Utils_UI = require("VSS/Utils/UI");
import Utils_Url = require("VSS/Utils/Url");
import Utils_Clipboard = require("VSS/Utils/Clipboard");
import DiscussionCommon = require("Presentation/Scripts/TFS/TFS.Discussion.Common");
import DiscussionCommonUI = require("Presentation/Scripts/TFS/TFS.Discussion.Common.UI");
import DiscussionResources = require("Presentation/Scripts/TFS/Resources/TFS.Resources.Discussion");
import ServerConstants = require("Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants");
import URI = require("Presentation/Scripts/URI");

import MentionMentionableControls = require("Mention/Scripts/TFS.Mention.Controls.Mentionable");

import AdornmentCommon = require("Presentation/Scripts/TFS/TFS.Adornment.Common");
import AdornmentMonacoUI = require("BuiltInExtensions/Scripts/TFS.Extensions.CodeEditor.Adornments");
import Q = require("q");

import Diag = require("VSS/Diag");
import VSS = require("VSS/VSS");
import VSS_Locations = require("VSS/Locations");

interface IContentOptions {
    filePath: string;
}

type IStandaloneEditor = monaco.editor.IStandaloneCodeEditor | monaco.editor.IStandaloneDiffEditor;

interface IEditorThemeInfo {
    themeName: string;
    themeData?: monaco.editor.IStandaloneThemeData;
}

/**
 * A custom Monaco Monarch language definition with the name used for registering the adhoc syntax highlighting.
 */
interface IMonarchLanguageInfo extends monaco.languages.IMonarchLanguage {
    name: string;
}

/**
 * Editor/Diff configuration options and other user preferences.
 */
interface IPreferences {
    editor: monaco.editor.IDiffEditorOptions;
    ignoreConfigurationChangedEvent: boolean;
    theme?: monaco.editor.BuiltinTheme;
}

/**
 * A flattened subset of Editor options that may be passed as user preferences and persisted by consuming classes.
 */
interface IUserPreferences {
    foldingEnabled?: boolean;
    minimapEnabled?: boolean;
    theme?: string;
    whiteSpaceEnabled?: boolean;
    wordWrapEnabled?: boolean;
}

/**
 * Item Content information for each side of the diff editor
 */
interface IDiffEditorItemInfo {
    path: string;
    version: string;
    content: string;
    contentType: string;
    error: string;
}

interface INewScrollPositionWithLine extends monaco.editor.INewScrollPosition {
    lineNumber?: number;
}

export class CodeEditor {

    private _versionControlExtension: TFS_Extension.VersionControlExtension = null;
    private _hostIdClass: string = "";
    private _isEditorInitialized = false;
    private _monacoContentTypesByExtension: any = null;
    private _contentLoadInProgress: boolean = false;
    private _contentLoadedDeferred: JQueryDeferred<any> = $.Deferred().resolve();
    private _currentConfig: any = {};
    private _diffEditorLeftItemInfo: IDiffEditorItemInfo = {} as IDiffEditorItemInfo;
    private _diffEditorRightItemInfo: IDiffEditorItemInfo = {} as IDiffEditorItemInfo;
    private _diffPerformedInitialScroll: boolean = false;
    private _throttledContentChangedTimeMs: number = 300;
    private _throttledContentChangedTimeout: number = null;
    private _maxFileContentLength: number = 5 * 1024 * 1024; // show up to 5 MB of text content (Monaco MODEL_SYNC_LIMIT is also 5 MB).;
    private _editorOptions: any = null;
    private _editorTopOffset: number = 0;
    private _editorFirstLineTop: number = 0;
    private _previousError: any = null;
    private _currentDiffSelection: any = null;
    private _discussionThreadControlManagersByEditorId: { [id: string]: DiscussionThreadControlManager } = {};
    private _adornmentControlManagersByEditorId: any = {};
    private _originalContent: string = null;
    private _originalContentType: string = null;
    private _originalContentPath: string = null;
    private _isDirty: boolean = false;
    private _diffEditorForEdit: monaco.editor.IDiffEditor = null;
    private _$diffEditorHost: JQuery = null;
    private _$editorHost: JQuery = null;
    private _diffEditMode: boolean = false;
    private _resizeRequested: boolean = false;
    private _contentChangedListener: monaco.IDisposable = null;
    private _escapeEditListener: monaco.IDisposable = null;
    private _lineLinkingWidgetsByEditorId: { [id: string]: LineLinkingWidget } = {};
    private _editorDisposables: monaco.IDisposable[] = [];
    private _diffEditorDisposables: monaco.IDisposable[] = [];
    private _customLanguages: { [name: string]: IMonarchLanguageInfo } = {};
    private _themeName: string;
    private _userPreferences: IUserPreferences = {};
    private _lastPreserveLineHeight: boolean;
    private _contributionLoader = new ContributionLoader();
    private _highlightableRanges: monaco.Range[] = [];

    private NUM_CHARS_FOR_ICON_PADDING = 3;
    private DEFAULT_MIN_LINE_CHARS_WIDTH = 5;
    private DEFAULT_FONT_SIZE = 12;
    private DEFAULT_MONACO_MODE = undefined; //Formerly text/plain - leave undefined so that new languages contributed to monaco get picked up automatically.

    /**
     * Used to provide a navigation context menu to report a bug
     * @param richNavContextMenu 
     * @param editor 
     */
    public setNavContextMenu(richNavContextMenu: IContextMenuItem[], editor: monaco.editor.IEditor) {
        richNavContextMenu.forEach(menuItem => {
            const standaloneEditor: monaco.editor.IStandaloneCodeEditor = editor as monaco.editor.IStandaloneCodeEditor;
            const navBugOptionId = menuItem.text.toLowerCase().replace(/\s/g, "-");
            const checkAction = standaloneEditor.getAction(navBugOptionId);
            if (!checkAction) {
                standaloneEditor.addAction({
                    id: navBugOptionId,
                    label: menuItem.text,
                    precondition: undefined,
                    keybindingContext: undefined,
                    contextMenuGroupId: menuItem.contextMenuGroup,
                    contextMenuOrder: 100.5,
                    run: () => {
                        window.open(menuItem.onClickRef);
                    }
                });
            }
        });
    }

    public isContentLoadInProgress() {
        /// <summary>
        ///     Is content currently being loaded by the editor
        /// </summary>
        ///   <returns type="Boolean">
        ///       True if an operation to load content is currently in progress.
        ///   </returns>
        return this._contentLoadInProgress;
    }

    public isDiffEditorInitialized() {
        /// <summary>
        ///     Is the diff editor currently initialized and scrolled to the first diff
        /// </summary>
        ///   <returns type="Boolean">
        ///       True if the diff editor is ready/initialized
        ///   </returns>
        return this._diffPerformedInitialScroll;
    }

    /**
     * Returns true if the Monaco editor has been initialized.
     * This method was added for functional test synchronization.
     */
    public isEditorInitialized() {
        return this._isEditorInitialized;
    }

    private setErrorMessage(editor, errorText) {
        /// <summary>
        ///     Show an error message with the given text (null/empty to hide the error message area)
        /// </summary>
        /// <param name="errorText" type="String">
        ///     Error message or null/empty to hide the message area
        /// </param>
        var $editorHost,
            $errorsHost,
            $editorElement;

        if (errorText !== this._previousError) {
            this._previousError = errorText;
            $editorHost = $(".code-editor-host" + this._hostIdClass),
                $errorsHost = $(".errors-host" + this._hostIdClass);

            if (errorText) {
                $errorsHost.empty();
                $("<div />").addClass("error-message").text(errorText).attr("title", errorText).appendTo($errorsHost);
                $errorsHost.show();

                $editorHost.addClass("with-error");
                $editorElement = $editorHost.children().eq(0);
                if ($editorElement.length) {
                    this._editorTopOffset = $editorElement.offset().top;
                }
            }
            else {
                $errorsHost.hide();
                $editorHost.removeClass("with-error");
                this._editorTopOffset = 0;
            }

            if (editor && this.getEditorOptions().monitorScroll) {
                this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.SCROLL_POSITION_CHANGED, this.getEditorScrollPosition(editor));
            }
        }
    }

    private getFetchErrorMessage(error: string): string {
        return Utils_String.format(TFSCodeEditorResources.FailedToGetContentWithError, error);
    }

    private checkSupportedVersion() {
        /// <summary>
        ///     Ensure that the current client version is supported by the monaco editor
        /// </summary>
        ///   <returns type="Boolean">
        ///       True if supported, otherwise false is returned and an error message is shown.
        ///   </returns>

        var version;

        if (Utils_UI.BrowserCheckUtils.isMsie()) {
            version = parseFloat(Utils_UI.BrowserCheckUtils.getVersion());
            if (version < 8) {
                this.setErrorMessage(null, TFSCodeEditorResources.IEVersionError);
                this._versionControlExtension.sendLoadError(TFSCodeEditorResources.IEVersionError);
                return false;
            }
        }

        return true;
    }

    public getEditorOptions() {
        /// <summary>
        ///     Get the json options from the request for this extension
        /// </summary>
        ///   <returns type="Object">
        ///       JSON options posted to this extension
        ///   </returns>
        if (!this._editorOptions) {
            let editorDefaults = <monaco.editor.IEditorOptions>{
                fontSize: this.DEFAULT_FONT_SIZE,
                lineNumbersMinChars: this.DEFAULT_MIN_LINE_CHARS_WIDTH,
                
                // Enable extensions to provide a semantic colorization
                // extension.
                'semanticHighlighting.enabled': true
            };
            this._editorOptions = JSON.parse($(".code-editor-options" + this._hostIdClass).html() || "{}");
            this._editorOptions.configuration = $.extend(editorDefaults, this._editorOptions.configuration);
        }
        return this._editorOptions;
    }

    /**
     * Gets the Monaco theme information.  This can be based on 'vs' | 'vs-dark' | 'hc-black' with additional rules that extend/override the built-in themes
     */
    private getEditorTheme(extensionCss?): IEditorThemeInfo {
        let editorOptions = this.getEditorOptions();
        let editorThemeInfo: IEditorThemeInfo = editorOptions.editorThemeInfo;
        if (!editorThemeInfo) {
            let monacoTheme: monaco.editor.BuiltinTheme = editorOptions.editorTheme || "vs";
            const monacoEnvironmentService = getLWPService("IMonacoEnvironmentService");
            const themeName = monacoEnvironmentService.defineEditorTheme(monacoTheme);
            return { themeName };
        }
        return editorThemeInfo;
    }

    /**
     * As of Monaco 0.9.0, if using Edge with a Windows High Contrast Theme, the default "vs" and "vs-dark" themes will honor those HC colors.
     */
    private isEdgeWithWindowsHighContrastTheme(): boolean {
        if (Utils_UI.BrowserCheckUtils.isEdge()) {
            if (window.matchMedia && window.matchMedia("(-ms-high-contrast:active)").matches) {
                return true;
            }
        }
        return false;
    }

    private getContentType(contentInfo: any): string {
        /// <summary>
        ///     Get the proposed content type of the item fetched from TFS based on its metadata and/or checking its contents
        ///     for implications of a particular file type such as XML (vs plain text) and an Objective-C header file (vs C++).
        /// </summary>
        /// <param name="contentInfo" type="any">
        ///     The TFS FileContent object containing the content and metadata.
        /// </param>
        ///   <returns type="String">
        ///       The suggested content type from the file metadata.
        ///   </returns>
        var contentType: string = contentInfo.metadata ? contentInfo.metadata.contentType || "" : "";
        contentType = contentType.toLowerCase();

        // If the contentType is unknown (or raw octet-stream), then default to plain text
        if (!contentType || contentType === "application/octet-stream") {
            contentType = this.DEFAULT_MONACO_MODE;
        }

        // If the contentType is plain text, then check the item contents for any clues of a more specific type.
        if (contentType === this.DEFAULT_MONACO_MODE) {
            var content: string = contentInfo.content;

            if (content && content.length > 5) {

                // XML: Check if it begins with the XML prolog "<?xml"
                if (content.substring(0, 5).toLowerCase() === "<?xml") {
                    contentType = "text/xml";
                }

                // Objective-C: Check if it's an Objective-C .h header file, versus the default C++ for .h by looking for the "@interface" syntax.
                else if (contentInfo.metadata && contentInfo.metadata.extension === "h") {
                    var interfaceIndex = content.indexOf("@interface");
                    var endIndex = interfaceIndex > - 1 ? content.lastIndexOf("@end") : 0;
                    if (endIndex > 0 && endIndex > interfaceIndex) {
                        contentType = "text/x-objective-c";
                    }
                }
            }
        }

        return contentType;
    }

    private getMonacoContentTypeOverride(fileExtension: string): string {
        /// <summary>
        ///     Get a monaco-supported content-type override based on a given file extension
        /// </summary>
        /// <param name="fileExtension" type="String">
        ///     A string containing the file extension such as "html".
        /// </param>
        ///   <returns type="String">
        ///       The content type that you can pass to Monaco or undefined
        ///   </returns>
        if (this._monacoContentTypesByExtension === null) {
            this._monacoContentTypesByExtension = {};

            this._monacoContentTypesByExtension.bat = "text/x-bat";
            this._monacoContentTypesByExtension.c = "text/x-cpp";
            this._monacoContentTypesByExtension.cc = "text/x-cpp";
            this._monacoContentTypesByExtension.cmd = "text/x-bat";
            this._monacoContentTypesByExtension.coffee = "text/x-coffeescript";
            this._monacoContentTypesByExtension.config = "text/xml";
            this._monacoContentTypesByExtension.cpp = "text/x-cpp";
            this._monacoContentTypesByExtension.cs = "text/x-csharp";
            this._monacoContentTypesByExtension.cshtml = "text/x-cshtml";
            this._monacoContentTypesByExtension.csproj = "text/xml";
            this._monacoContentTypesByExtension.css = "text/css";
            this._monacoContentTypesByExtension.cxx = "text/x-cpp";
            this._monacoContentTypesByExtension.dockerfile = "text/x-dockerfile";
            this._monacoContentTypesByExtension.fs = "text/x-fsharp";
            this._monacoContentTypesByExtension.fsx = "text/x-fsharp";
            this._monacoContentTypesByExtension.go = "text/x-go";
            this._monacoContentTypesByExtension.h = "text/x-cpp";
            this._monacoContentTypesByExtension.handlebars = "text/x-handlebars-template";
            this._monacoContentTypesByExtension.hpp = "text/x-cpp";
            this._monacoContentTypesByExtension.htm = "text/html";
            this._monacoContentTypesByExtension.html = "text/html";
            this._monacoContentTypesByExtension.ini = "text/ini";
            this._monacoContentTypesByExtension.jade = "text/x-jade";
            this._monacoContentTypesByExtension.java = "text/x-java-source";
            this._monacoContentTypesByExtension.js = "text/javascript";
            this._monacoContentTypesByExtension.json = "application/json";
            this._monacoContentTypesByExtension.jsx = "text/javascript";
            this._monacoContentTypesByExtension.kt = "text/x-kotlin";
            this._monacoContentTypesByExtension.less = "text/x-less";
            this._monacoContentTypesByExtension.lua = "text/x-lua";
            this._monacoContentTypesByExtension.m = "text/x-objective-c";
            this._monacoContentTypesByExtension.mm = "text/x-objective-c";
            this._monacoContentTypesByExtension.markdown = "text/x-markdown";
            this._monacoContentTypesByExtension.md = "text/x-markdown";
            this._monacoContentTypesByExtension.mdown = "text/x-markdown";
            this._monacoContentTypesByExtension.mjs = "text/javascript";
            this._monacoContentTypesByExtension.php = "application/x-php";
            this._monacoContentTypesByExtension.ps1 = "text/x-powershell";
            this._monacoContentTypesByExtension.psm1 = "text/x-powershell";
            this._monacoContentTypesByExtension.py = "text/x-python";
            this._monacoContentTypesByExtension.r = "text/x-r";
            this._monacoContentTypesByExtension.rb = "text/x-ruby";
            this._monacoContentTypesByExtension.rc = "text/x-cpp";
            this._monacoContentTypesByExtension.rhistory = "text/x-r";
            this._monacoContentTypesByExtension.rlib = "text/x-rust";
            this._monacoContentTypesByExtension.rs = "text/x-rust";
            this._monacoContentTypesByExtension.rt = "text/x-r";
            this._monacoContentTypesByExtension.sass = "text/x-scss";
            this._monacoContentTypesByExtension.scss = "text/x-scss";
            this._monacoContentTypesByExtension.sql = "text/x-sql";
            this._monacoContentTypesByExtension.strada = "text/strada";
            this._monacoContentTypesByExtension.swift = "text/swift";
            this._monacoContentTypesByExtension.ts = "text/typescript";
            this._monacoContentTypesByExtension.tsx = "text/typescript";
            this._monacoContentTypesByExtension.vb = "text/x-vb";
            this._monacoContentTypesByExtension.xaml = "application/xaml+xml";
            this._monacoContentTypesByExtension.xml = "text/xml";
            this._monacoContentTypesByExtension.yaml = "application/x-yaml";
            this._monacoContentTypesByExtension.yml = "application/x-yaml";
        }
        return this._monacoContentTypesByExtension[("" + fileExtension).toLowerCase()];
    }

    private beginGetMonacoMode(contentType, options: IContentOptions, callback: (mode: string) => void) {
        /// <summary>
        ///     Get the Monaco-supported content type mode to show for the given file, ensuring
        ///     that necessary Monaco modules have been loaded
        /// </summary>
        /// <param name="contentType" type="String|IMonarchLanguage">
        ///     File content type (e.g. "application/xml") or a Monarch language definition
        /// </param>
        /// <param name="options" type="IContentOptions">
        ///     Options for getting Monaco mode, which has the following properties:
        ///       - filePath, the path to the file (e.g. "$/project/file.xml")
        /// </param>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method called after the content has been set with the
        ///     "mode" string that should be passed to the Monaco editor options
        /// </param>
        if (!!contentType && typeof contentType !== "string") {

            // As of Monaco 0.8.3, IMonarchLanguage must first be registered then referenced by name.
            // For example, this is used by Build for syntax highlighting of build logs.
            const languageInfo = contentType as IMonarchLanguageInfo;
            if (languageInfo.name) {
                languageInfo.name = languageInfo.name.toLowerCase().replace(/[^a-z]/g, "");
                if (!this._customLanguages[languageInfo.name]) {
                    languageInfo.tokenPostfix = languageInfo.tokenPostfix || "";
                    monaco.languages.register({ id: languageInfo.name });
                    monaco.languages.setMonarchTokensProvider(languageInfo.name, languageInfo);
                    this._customLanguages[languageInfo.name] = languageInfo;
                }
                callback(languageInfo.name);
                return;
            }

            // contentType is not a recongized type or missing the name, so render as plain text.
            callback(this.DEFAULT_MONACO_MODE);
            return;
        }

        // For some known file extensions, check for a monaco-specific
        // content types (such as .cpp --> application/x-cpp)
        // with some exceptions, such as "text/x-objective-c", which were determined by examining the file contents.
        if (contentType !== "text/x-objective-c" && options) {
            var filePath = options.filePath;
            var index = ("" + filePath).lastIndexOf('.');
            if (index >= 0) {
                contentType = this.getMonacoContentTypeOverride(filePath.substr(index + 1)) || contentType;
            }
        }

        if (!contentType || contentType === this.DEFAULT_MONACO_MODE) {
            callback(this.DEFAULT_MONACO_MODE);
            return;
        }

        callback(contentType);
    }

    private sendEditorContentChangedEvent(editor) {
        /// <summary>
        ///     Sends the current editor content-changed event and dirty-state-changed event (if applicable) to the TFS host
        /// </summary>

        // Change Event: Reset the timeout and prepare to send a content-changed event.
        if (this._throttledContentChangedTimeout) {
            window.clearTimeout(this._throttledContentChangedTimeout);
        }
        this._throttledContentChangedTimeout = window.setTimeout(() => {
            const currentContent = editor.getValue();
            this._versionControlExtension.sendEditorContentChangedEvent(this._contentLoadInProgress, currentContent);

            if (!this._contentLoadInProgress) {
                const isDirty = currentContent !== this._originalContent;
                if (isDirty !== this._isDirty) {
                    this._isDirty = isDirty;
                    this._versionControlExtension.sendEditorDirtyStateChangedEvent(isDirty);
                }
            }
        }, this._isDirty ? this._throttledContentChangedTimeMs : 0);
    }

    private getEditorScrollPosition(editor: monaco.editor.ICodeEditor) {
        /// <summary>
        ///     Returns the current editor's scroll position
        /// </summary>
        const ranges = editor.getVisibleRanges();
        return {
            scrollTop: editor.getScrollTop(),
            scrollLeft: editor.getScrollLeft(),
            scrollLineTop: ranges && ranges[0] && ranges[0].startLineNumber,
            lineHeight: editor.getOption(monaco.editor.EditorOption.lineHeight),
            topOffset: this._editorTopOffset,
            firstLineTop: this._editorFirstLineTop,
        };
    }

    private setEditorScrollPosition(editor: monaco.editor.ICodeEditor, position: INewScrollPositionWithLine) {
        if (position.lineNumber != undefined) {
            editor.revealRangeAtTop({ startLineNumber: position.lineNumber, endLineNumber: position.lineNumber, startColumn: 1, endColumn: 1 } as monaco.IRange);
        } else {
            editor.setScrollPosition(position);
        }
    }

    private getEditorSelection(editor: monaco.editor.ICodeEditor) {
        /// <summary>
        ///     Returns the current editor's selection state
        /// </summary>
        return editor.getSelection();
    }

    private updateEditorTheme(editor: monaco.editor.ICodeEditor, config: any) {

        if (config.editorTheme) {
            const editorOptions = this.getEditorOptions();
            editorOptions.editorThemeInfo = undefined;
            editorOptions.editorTheme = config.editorTheme;
        }

        let newTheme = this.getEditorTheme(config.editorCss).themeName;

        if (newTheme !== this._themeName) {

            // Save off the current line height
            let oldLineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);

            // Update the theme.  Themes were changed in Monaco v0.9.0 to be global for all instances.
            // There's a setTheme() API method, but they apparently forgot getTheme(), so we track it with _themeName.
            this._themeName = newTheme;
            monaco.editor.setTheme(this._themeName);

            // If the line height was changed, updated the scroll offset
            // so that the top line is the same as it was before
            let newLineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);
            if (newLineHeight !== oldLineHeight) {
                let scrollTop = editor.getScrollTop();
                if (scrollTop !== 0) {
                    scrollTop = scrollTop * newLineHeight / oldLineHeight;
                    editor.setScrollTop(scrollTop);
                }
            }
        }
    }

    private updateEditorReadOnly(editor: monaco.editor.ICodeEditor, config: any) {
        const editorReadOnly = editor.getOption(monaco.editor.EditorOption.readOnly);
        var readOnly = config.editable !== true;
        if (editorReadOnly !== readOnly) {
            editor.updateOptions({ readOnly: readOnly });
        }

        if (readOnly) {
            if (this._contentChangedListener) {
                this._contentChangedListener.dispose();
                this._contentChangedListener = null;
            }
            if (this._escapeEditListener) {
                this._escapeEditListener.dispose();
                this._escapeEditListener = null;
            }
        }
        else {
            // For keyboard accessibility, the Esc key provides notification to get the focus out of Monaco when editing
            this._escapeEditListener = editor.onKeyDown((e: monaco.IKeyboardEvent) => {
                if (e.keyCode === monaco.KeyCode.Escape &&
                    !e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    this.sendEscapeEditEvent();
                }
            });
            this.addContentChangedListener(editor);
            if (!config.noFocus) {
                if (config.showDiff) {
                    if (this._diffEditorForEdit) {
                        this._diffEditorForEdit.focus();
                    }
                } else {
                    editor.focus();
                }
            }
        }
    }

    private updateUserPreferences(editor: IStandaloneEditor, config: any) {
        const newPreferences = config.userPreferences as IUserPreferences || {};
        const havePreferencesChanged =
            newPreferences &&
            Object.keys(newPreferences).length > 0 &&
            (this._userPreferences.foldingEnabled !== newPreferences.foldingEnabled ||
                this._userPreferences.minimapEnabled !== newPreferences.minimapEnabled ||
                this._userPreferences.whiteSpaceEnabled !== newPreferences.whiteSpaceEnabled ||
                this._userPreferences.wordWrapEnabled !== newPreferences.wordWrapEnabled);

        if (havePreferencesChanged || this._lastPreserveLineHeight !== config.preserveLineHeight) {
            this._userPreferences = newPreferences;
            this._lastPreserveLineHeight = config.preserveLineHeight;

            this.toggleFolding(!config.preserveLineHeight && this._userPreferences.foldingEnabled);
            this.toggleMinimap(this._userPreferences.minimapEnabled);
            this.toggleWhiteSpace(this._userPreferences.whiteSpaceEnabled);
            this.toggleWordWrap(!config.preserveLineHeight && this._userPreferences.wordWrapEnabled);

            // TODO: update theme
            this.updateEditorOptions(editor);
        }
    }

    private addContentChangedListener(editor: monaco.editor.ICodeEditor) {
        // Don't add the listener again if we already have one.
        if (!this._contentChangedListener) {
            this._contentChangedListener = editor.onDidChangeModelContent(() => {
                this.sendEditorContentChangedEvent(editor);
            });
        }
    }

    // Sets the value of this._contentLoadInProgress.  When it is NOT loading (i.e., the loading completed), then the Deferred is resolved.
    private setContentLoadInProgress(isLoading: boolean) {
        this._contentLoadInProgress = isLoading;
        if (isLoading) {
            // Create a new this._contentLoadedDeferred if it has already been resolved or rejected so that new requests for content wait until it's loaded again.
            if (this._contentLoadedDeferred.state() !== "pending") {
                this._contentLoadedDeferred = $.Deferred();
            }
        }
        else {
            // Resolve the promise for those awaiting the loaded editor content
            this._contentLoadedDeferred.resolve();
        }
    }

    /** Sets the model for the Monaco CodeEditor, and disposes of the old one. */
    private setEditorModel(editor: monaco.editor.ICodeEditor, model: monaco.editor.IModel) {
        var oldModel: monaco.editor.IModel = editor.getModel();
        editor.setModel(model);
        this.disposeModel(oldModel);
    }

    /** Sets the original and modified models for the Monaco DiffEditor, and disposes of the old ones. */
    private setDiffEditorModels(diffEditor: monaco.editor.IDiffEditor, originalModel: monaco.editor.IModel, modifiedModel: monaco.editor.IModel) {
        var oldOriginalModel: monaco.editor.IModel = diffEditor.getOriginalEditor().getModel();
        var oldModifiedModel: monaco.editor.IModel = diffEditor.getModifiedEditor().getModel();

        if (originalModel === null && modifiedModel === null) {
            diffEditor.setModel(null);
        } else {
            diffEditor.setModel({
                original: originalModel,
                modified: modifiedModel
            });
        }

        this.disposeModel(oldOriginalModel);
        this.disposeModel(oldModifiedModel);
    }

    /** Disposes a Monaco model */
    private disposeModel(model: monaco.editor.IModel) {
        if (model && !model.isDisposed()) {
            model.dispose();
        }
    }

    public setEditorContent(editor: monaco.editor.ICodeEditor, content: string, clearLoadingFlag: boolean, contentType: string, resource?, callback?) {
        /// <summary>
        ///     Set the content of the editor
        /// </summary>
        /// <param name="editor" type="Monaco.Editor">
        ///     Monaco editor to modify
        /// </param>
        /// <param name="content" type="String">
        ///     File content
        /// </param>
        /// <param name="contentType" type="String">
        ///     File content type (e.g. "application/xml")
        /// </param>
        /// <param name="resource" type="{}">
        ///     The resource of content including path to the file and version.
        /// </param>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method called after the content has been set
        /// </param>
        this._originalContent = content;
        var options;
        if (resource) {
            options = {
                filePath: resource.path
            };
        }

        this._isDirty = false;
        this.beginGetMonacoMode(contentType, options, (mode) => {
            let urlPromise: IPromise<string> =
                resource ? getResourceService().then(service =>
                    service.getResourceUri(resource.repo, resource.version, resource.path)) : Q(undefined);
            urlPromise.then(url => {
                const parsedUrl = url ? monaco.Uri.parse(url) : undefined;
                if (parsedUrl) {
                    this._contributionLoader.reloadContributions(parsedUrl, this, editor);
                }
                this.setEditorModel(editor, monaco.editor.createModel(content || "", mode, url ? monaco.Uri.parse(url) : undefined));

                editor.layout();
                if (clearLoadingFlag) {
                    this.setContentLoadInProgress(false);
                }

                if (this._currentConfig.editable) {
                    if (!this._currentConfig.noFocus) {
                        if (this._currentConfig.showDiff) {
                            if (this._diffEditorForEdit) {
                                this._diffEditorForEdit.focus();
                            }
                        } else {
                            editor.focus();
                        }
                    }
                }

                if (callback) {
                    callback.call(this);
                }
            });
        });
    }

    /**
     * Create a built-in code editor (Monaco) that can co-exist with other editors within page divs (not iframes).
     * @param callback Callback method which accepts the editor as a parameter.
     * @param versionControlExtension
     */
    public beginCreateBuiltInEditor(callback: IResultCallback, versionControlExtension: TFS_Extension.VersionControlExtension) {
        TFSAdvancedCodeEditorPlugin.setModuleCodeEditor(this);
        this.beginCreateEditor(callback, versionControlExtension);
    }

    public beginCreateEditor(callback, versionControlExtension?: TFS_Extension.VersionControlExtension) {
        /// <summary>
        ///     Ensure the required Monaco modules are loaded, and create an editor
        /// </summary>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method which accepts the editor as a parameter.
        /// </param>

        if (!this.checkSupportedVersion()) {
            return;
        }

        const performanceScenario = getScenarioManager().startScenario("VersionControl", "CodeEditor.beginCreateEditor");

        versionControlExtension = versionControlExtension || TFS_Extension.getVersionControlExtension();
        this._versionControlExtension = versionControlExtension;
        this.setHostIdClass();

        this.requireMonaco(() => {
            // No-op if ".code-editor-host" isn't found. Most likely, the DOM was emptied from SPA navigation before the Monaco scripts loaded.
            const $editorHost: JQuery = $(".code-editor-host" + this._hostIdClass);
            if (!$editorHost[0]) {
                return;
            }

            this._$editorHost = $editorHost;
            const editorOptions = this.getEditorOptions();

            const editor: monaco.editor.IStandaloneCodeEditor = monaco.editor.create($editorHost[0],
                ($.extend(editorOptions.configuration || {}, <monaco.editor.IEditorConstructionOptions>{
                    value: "",
                    mode: this.DEFAULT_MONACO_MODE,
                    readOnly: editorOptions.editable !== true,
                    scrollBeyondLastLine: editorOptions.editable === true,
                    theme: this.getEditorTheme().themeName,
                    wrappingColumn: -1,
                    longLineBoundary: 1e6,
                    fixedOverflowWidgets: true,
                    scrollbar: {
                        horizontalScrollbarSize: 14,
                        verticalScrollbarSize: 16
                    },
                })));

            preferences.editor.minimap.enabled = preferences.editor.minimap.enabled || editorOptions.minimapEnabled === true;
            preferences.editor.folding = preferences.editor.folding || editorOptions.foldingEnabled === true;
            this.updateEditorOptions(editor);

            $editorHost.data("editor", editor);
            $editorHost.on("remove", () => {
                this._isEditorInitialized = false;
                this.setEditorModel(editor, null);
                editor.dispose();
            });

            editor.onDidDispose(() => {
                disposeDisposables(this._editorDisposables);
                this._editorDisposables = [];
            });

            if (editorOptions.editable === true) {
                this.addContentChangedListener(editor);
            }

            if (editorOptions.monitorScroll) {
                this._editorDisposables.push(editor.onDidScrollChange((e: monaco.IScrollEvent) => {
                    // scrollTopChanged may indicate a widget being displayed/hidden,
                    // which may add empty area above the first line.
                    if (e.scrollTopChanged) {
                        this._editorFirstLineTop = editor.getTopForLineNumber(1);
                    }

                    this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.SCROLL_POSITION_CHANGED, this.getEditorScrollPosition(editor));
                }));
            }

            if (editorOptions.monitorSelection) {
                this._editorDisposables.push(editor.onDidChangeCursorPosition((e: monaco.editor.ICursorPositionChangedEvent) => {
                    this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.SELECTION_CHANGED, this.getEditorSelection(editor));
                }));
            }

            // This editorOptions.bindKeys were previously used by Search when Monaco was hosted in an IFrame (and subsequently broken by Monaco API changes).
            // Since Monaco is typically no longer hosted in an IFrame (except by Release Management who is currently updating their UI),
            // the consumer can listen for key events directly, as Search now does for Next/Prev F8 keys.
            // However, bindKeys have been fixed and left for possible use with a check for the required numeric key binding based on KeyCode values in monaco.d.ts.
            const sendKeyDownEvent = this.sendKeyDownEvent;
            if (editorOptions.bindKeys) {
                for (var i = 0; i < editorOptions.bindKeys.length; i++) {
                    const keyBinding = editorOptions.bindKeys[i];
                    if (typeof keyBinding === "number") {
                        editor.addCommand(keyBinding,
                            function (index) {
                                return function () {
                                    sendKeyDownEvent(editorOptions.bindKeys[index]);
                                };
                            }(i), null);
                    }
                }
            }

            // Add context menu items applicable for all code editor instances
            this.addBuiltInContextMenuItems(editor);

            // if custom context menu items are specified, then add them to the editor's context menu
            if (editorOptions.contextMenuItems) {
                this.addContextMenuItems(editorOptions.contextMenuItems, editor);
            }

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.EDITOR_ADD_CONTEXT_MENU_ITEM, (data) => {
                if (data.actionData.contextMenuItems) {
                    this.addContextMenuItems(data.actionData.contextMenuItems, editor);
                }
            });

            $(window).resize(() => {
                if (!this._diffEditMode) {
                    this.layoutFileEditor(editor);
                } else {
                    this._resizeRequested = true;
                }
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_EDITOR_CONTENT, (message) => {
                // Waits to return the editor contents if there is a load in progress.
                this._contentLoadedDeferred.done(() => {
                    this._versionControlExtension.sendEditorContent(editor.getValue((message.actionData || {}).codeEditorOptions), message.requestId);
                });
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_SCROLL_POSITION, (message) => {
                this._versionControlExtension.sendResponse(message.requestId, TFS_Extension.VersionControl.ActionIds.GET_SCROLL_POSITION, this.getEditorScrollPosition(editor));
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.SET_SCROLL_POSITION, (message) => {
                this.setEditorScrollPosition(editor, message.actionData);
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_SELECTION, (message) => {
                this._versionControlExtension.sendResponse(message.requestId, TFS_Extension.VersionControl.ActionIds.GET_SELECTION, this.getEditorSelection(editor));
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.REFRESH_LAYOUT, (message) => {
                editor.layout();
            });

            this._versionControlExtension.onResetEditorDiffContent(() => {
                this._originalContent = editor.getValue();
                this.showEditDiff(false);
            });

            // Notifying completion of editor creation if there are any config that needs to be added after editor creation.
            this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.EDITOR_CREATED);

            if (callback) {
                callback.call(this, editor);
            }

            performanceScenario.end();

            return editor;
        });
    }

    /**
     * Sets and uses a unique class name based on the parent div/host id to allow multiple code editors on the same page
     */
    private setHostIdClass() {
        var hostId = this._versionControlExtension.getHostId();
        this._hostIdClass = hostId ? ".code-editor-hostId-" + hostId : "";
    }

    private requireMonaco(callback) {
        const contributionService = getLWPService("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-features.editor").then(() => {
            const monacoEnvironmentService = getLWPService("IMonacoEnvironmentService");
            monacoEnvironmentService.setupMonacoEnvironment();

            const editor = callback();
            this._contributionLoader.loadContributions(this, editor);
        });
    }

    private addContextMenuItems(contextMenuItemsList: monaco.editor.IActionDescriptor[], editor: monaco.editor.IStandaloneCodeEditor) {
        if (!editor || !contextMenuItemsList || !contextMenuItemsList.length) {
            return;
        }

        contextMenuItemsList.forEach((menuItem, index) => {
            if (!editor.getAction(menuItem.id)) {
                editor.addAction({
                    id: menuItem.id,
                    label: menuItem.label,
                    contextMenuGroupId: menuItem.contextMenuGroupId,
                    keybindings: menuItem.keybindings,
                    keybindingContext: undefined,
                    run: (editor: monaco.editor.ICodeEditor) => {
                        var dataObject = this.getDataForContextMenuItemAction(editor);
                        var contextMenuItemClickEventObject = {
                            contextMenuItemId: menuItem.id,
                            dataForAction: dataObject
                        };

                        this.sendContextMenuItemClickEvent(contextMenuItemClickEventObject);
                        return null;
                    }
                });
            }
        });
    }

    /**
     * Add context menu items applicable for all code editor instances.  These are visible in Monaco's F1 Command Palette.
     */
    private addBuiltInContextMenuItems(editor: IStandaloneEditor) {
        if (editor) {

            const diffEditor = isIDiffEditor(editor) ? editor as monaco.editor.IStandaloneDiffEditor : null;
            const codeEditor = diffEditor ? diffEditor.getModifiedEditor() : editor as monaco.editor.IStandaloneCodeEditor;
            let action: monaco.editor.IActionDescriptor;

            // Toggle Code Folding
            // Diff editor: Do not allow code folding.  Alignment is obviously wrong when side-by-side, and can be misleading/confusing when inline.
            if (!diffEditor) {
                action = {
                    id: "tfs-toggle-folding",
                    label: TFSCodeEditorResources.ContextMenuToggleCodeFolding,
                    keybindings: [monaco.KeyMod.chord(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_K, monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_K)],
                    precondition: "!preserveLineHeight",
                    run: (e: monaco.editor.ICodeEditor) => {
                        this._userPreferences.foldingEnabled = this.toggleFolding(codeEditor);
                        this.updateEditorOptions(diffEditor || codeEditor);
                        this.sendEditorPreferenceChangedEvent({ foldingEnabled: this._userPreferences.foldingEnabled });
                    },
                };
                codeEditor.addAction(action);
                diffEditor && diffEditor.getOriginalEditor().addAction(action);
            }

            // Toggle Minimap
            // Diff Editor:  Add the context menu item to both sides, but only allow showing the minimap for the modified side.
            // This is consistent with only showing diff markers on the modified side and reduces space consumption.
            action = {
                id: "tfs-toggle-minimap",
                label: TFSCodeEditorResources.ContextMenuToggleMinimap,
                run: (e: monaco.editor.ICodeEditor) => {
                    this._userPreferences.minimapEnabled = this.toggleMinimap(codeEditor);
                    this.updateEditorOptions(diffEditor || codeEditor);
                    this.sendEditorPreferenceChangedEvent({ minimapEnabled: this._userPreferences.minimapEnabled });
                },
            };
            codeEditor.addAction(action);
            diffEditor && diffEditor.getOriginalEditor().addAction(action);

            // Toggle Word Wrap
            // Diff editor: Turn on word wrap only with inline view (not side by side because of obvious alignment issues).
            // There will still be alignment issues of the diff indicators in the scroll, but it's a tradeoff for now.
            action = {
                id: "tfs-toggle-wordwrap",
                label: TFSCodeEditorResources.ContextMenuToggleWordWrap,
                precondition: "!preserveLineHeight",
                run: (e: monaco.editor.ICodeEditor) => {
                    this._userPreferences.wordWrapEnabled = this.toggleWordWrap(codeEditor);
                    this.updateEditorOptions(diffEditor || codeEditor);
                    this.sendEditorPreferenceChangedEvent({ wordWrapEnabled: this._userPreferences.wordWrapEnabled });
                },
            };
            codeEditor.addAction(action);
            diffEditor && diffEditor.getOriginalEditor().addAction(action);

            // Toggle White Space
            // Diff editor: Turn on white space rendering in both sides. Also turn on diff'ing whitespace (don't ignore it).
            action = {
                id: "tfs-toggle-whitespace",
                label: TFSCodeEditorResources.ContextMenuToggleWhiteSpace,
                run: (e: monaco.editor.ICodeEditor) => {
                    this._userPreferences.whiteSpaceEnabled = this.toggleWhiteSpace(codeEditor);
                    this.updateEditorOptions(diffEditor || codeEditor);
                    this.sendEditorPreferenceChangedEvent({ whiteSpaceEnabled: this._userPreferences.whiteSpaceEnabled });
                },
            };
            codeEditor.addAction(action);
            diffEditor && diffEditor.getOriginalEditor().addAction(action);
        }
    }

    private toggleFolding(value: boolean | monaco.editor.IStandaloneCodeEditor): boolean {
        if (isNullOrUndefined(value)) {
            return false;
        }
        const enable = typeof (value) === "boolean" ? value as boolean
            : !(value as monaco.editor.IStandaloneCodeEditor).getOption(monaco.editor.EditorOption.folding)
        preferences.editor.folding = enable;
        return enable;
    }

    private toggleMinimap(value: boolean | monaco.editor.IStandaloneCodeEditor): boolean {
        if (isNullOrUndefined(value)) {
            return false;
        }
        const enable = typeof (value) === "boolean" ? value as boolean
            : !(value as monaco.editor.IStandaloneCodeEditor).getOption(monaco.editor.EditorOption.minimap).enabled
        preferences.editor.minimap.enabled = enable;
        return enable;
    }

    private toggleWhiteSpace(value: boolean | monaco.editor.IStandaloneCodeEditor): boolean {
        if (isNullOrUndefined(value)) {
            return false;
        }
        const enable = typeof (value) === "boolean" ? value as boolean
            : (value as monaco.editor.IStandaloneCodeEditor).getOption(monaco.editor.EditorOption.renderWhitespace) === 'none';
        preferences.editor.renderWhitespace = enable ? 'all' : 'none';
        preferences.editor.ignoreTrimWhitespace = !enable;
        return enable;
    }

    private toggleWordWrap(value: boolean | monaco.editor.IStandaloneCodeEditor): boolean {
        if (isNullOrUndefined(value)) {
            return false;
        }
        const enable = typeof (value) === "boolean" ? value as boolean
            : !(value as monaco.editor.IStandaloneCodeEditor).getOption(monaco.editor.EditorOption.wrappingInfo).isViewportWrapping
        preferences.editor.wordWrap = enable ? 'on' : 'off';
        return enable;
    }

    /**
     * update the editor/diff with the preferred configuration options
     */
    private updateEditorOptions(editor: IStandaloneEditor) {
        preferences.ignoreConfigurationChangedEvent = true;

        if (isIDiffEditor(editor)) {
            const diffEditor = editor as monaco.editor.IStandaloneDiffEditor;
            const options = this.getAdjustedDiffEditorOptions();
            diffEditor.updateOptions(options);

            // diffEditor.updateOptions() turns off word wrapping and the minimap.
            // We can turn them back on if needed in the individual editors.
            if (options.minimap.enabled || options.wordWrap === "on") {
                diffEditor.getModifiedEditor().updateOptions(options);
                options.minimap.enabled = false;
                if (options.wordWrap === "on") {
                    options.lineNumbers = 'off';
                    options.wordWrap = 'off';
                    diffEditor.getOriginalEditor().updateOptions(options);
                }
            }
        }
        else {
            editor.updateOptions(preferences.editor);
        }

        preferences.ignoreConfigurationChangedEvent = false;
    }

    /**
     * Return a clone of the preferred editor options with adjustments made as required for the diff viewer.
     */
    private getAdjustedDiffEditorOptions(): monaco.editor.IDiffEditorOptions {
        return {
            ...preferences.editor,
            minimap: { ...preferences.editor.minimap },
            wordWrap: preferences.editor.renderSideBySide ? 'off' : preferences.editor.wordWrap,
            folding: false,
        }
    }

    private sendKeyDownEvent(keysPressed) {
        this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.EDITOR_KEY_PRESSED, keysPressed);
    }

    private sendEscapeEditEvent() {
        this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.EDITOR_ESCAPE_EDIT);
    }

    private sendContextMenuItemClickEvent(contextMenuClickEventObject) {
        this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.EDITOR_CONTEXT_MENU_ITEM_CLICKED, contextMenuClickEventObject);
    }

    private sendEditorPreferenceChangedEvent(preferences: IUserPreferences) {
        this._versionControlExtension.sendEditorPreferencesChangedEvent(preferences);
    }

    private getDataForContextMenuItemAction(editor: monaco.editor.ICodeEditor) {
        /// <summary>
        ///     Method returns the data to act upon context menu item click.
        ///     This method can be extended to return the data required for specfic context menu item clicks.
        ///     Current implementation returns the text under the selection/cursor.
        /// </summary>
        /// <param name="editor" type="monaco.editor.ICodeEditor">
        ///     instance of the monaco editor.
        /// </param>

        var data: any = {};

        // get text under selection
        var selection: monaco.Selection = editor.getSelection(),
            text: string = "",
            model: monaco.editor.IModel = editor.getModel(),
            selectionEndLineNumber: number = Math.max(selection.startLineNumber, selection.endLineNumber),
            selectionStartLineNumber: number = Math.min(selection.startLineNumber, selection.endLineNumber),
            selectionStartColumnNumber: number = Math.min(selection.startColumn, selection.endColumn),
            selectionEndColumnNumber: number = Math.max(selection.startColumn, selection.endColumn);

        if (selectionEndLineNumber > selectionStartLineNumber ||
            selectionEndColumnNumber > selectionStartColumnNumber) {
            text = model.getValueInRange(selection);
        }
        else {
            // There is no selection. The cursor focuses a word.
            var wordAtPosition: monaco.editor.IWordAtPosition = model.getWordAtPosition(<monaco.IPosition>{
                lineNumber: selectionStartLineNumber,
                column: selectionStartColumnNumber
            });

            // wordAtPosition is null if cursor is not placed over any text. e.g. end of line.
            if (wordAtPosition) {
                text = wordAtPosition.word;
            }
        }

        data["textUnderCursorOrSelection"] = text;

        return data;
    }

    private layoutFileEditor(editor) {
        this._resizeRequested = false;
        if (!this._versionControlExtension.getConfiguration().skipLayoutOnResize &&
            this._isEditorVisible(editor)) {
            editor.layout();
        }
    }

    private _isEditorVisible(editor) {
        if (!editor._domElement) {
            Diag.logWarning("TFS.ExtensionsCodeEditor: editor._domElement is not available. Perhaps an upgrade has changed this editor private attribute.");
        }

        return $(editor._domElement).is(":visible");
    }

    public setEditorConfig(editor, config) {
        /// <summary>
        ///     Sets the Editor configuration. This makes calls to retrieve the VC item's
        ///     contents and sets the editor content.
        /// </summary>
        /// <param name="editor" type="Monaco.Editor">
        ///     Editor to configure
        /// </param>
        /// <param name="config" type="Object">
        ///     Extension configuration
        /// </param>

        this.updateEditorTheme(editor, config);
        this.updateEditorReadOnly(editor, config);
        this.updateUserPreferences(editor, config);
        this.updateCommonEditorConfig(editor, false, config);

        editor.createContextKey("preserveLineHeight", config.preserveLineHeight);

        if (config.activeState === false) {
            // The view is now inactive. Remember the config but don't update anything for now.
            this._currentConfig = config;
            return;
        }

        if (config.showDiff) {
            // This mode is used when editing a file and wanting to show diff from unedited content
            this._diffEditMode = true;
            if (!this._$diffEditorHost) {
                var parentHost = $('.code-editor-host').parent();
                this._$diffEditorHost = $('<div></div>').addClass('code-editor-host').appendTo(parentHost);
                this.beginCreateDiffEditor((diffEditor: monaco.editor.IDiffEditor) => {
                    this._diffEditorForEdit = diffEditor;
                    this.updateDiffSideBySide(this._diffEditorForEdit, config);

                    if (config.editable) {
                        // Initialize to enable editing in diff mode, and keep the original editor's content in synch.
                        var modifiedEditor = diffEditor.getModifiedEditor();
                        modifiedEditor.updateOptions({ readOnly: false });
                        modifiedEditor.onDidChangeModelContent((e: monaco.editor.IModelContentChangedEvent) => {
                            editor.setValue(modifiedEditor.getValue());
                        });
                    }

                    this.renderEditDiff(editor);
                }, this._versionControlExtension);
            }
            else if (this._diffEditorForEdit) {
                this.renderEditDiff(editor);
            }
            this.updateDiffSideBySide(this._diffEditorForEdit, config);
            this._currentConfig = config;
            return;

        } else if (config.path &&
            config.path === this._currentConfig.path &&
            config.version === this._currentConfig.version &&
            typeof config.content !== "string" &&
            !config.forceRefresh &&
            (!config.repositoryId || config.repositoryId === this._currentConfig.repositoryId)) {
            this._diffEditMode = false;
            this.showEditDiff(false);

            // Already showing this file. Don't need to re-fetch contents
            if (!this._contentLoadInProgress) {
                this.setDiscussionThreads(editor, config.discussionThreads, config.selectedDiscussionId);
                this.setAdornments(editor, config.adornments);
            }

            this.layoutFileEditor(editor);
            this._currentConfig = config;
            return;
        }

        this._diffEditMode = false;
        this.showEditDiff(false);

        this._currentConfig = config;
        this.setContentLoadInProgress(true);
        this.setDiscussionThreads(editor, []);
        this.setAdornments(editor, []);
        this.setErrorMessage(editor, null);

        if (typeof config.content === "string") {
            // Content explicitly provided
            var contentType = config.contentType || (config.fileInfo || {}).contentType || this.DEFAULT_MONACO_MODE;
            var resource = !config.skipResource ? { repo: config.repositoryId, version: config.version, path: config.path } : null;
            this.setEditorContent(editor, config.content, true, contentType, resource, () => {
                this.updateLineNumbersWidth(editor, false, config);
                this.setDiscussionThreads(editor, config.discussionThreads, config.selectedDiscussionId);
                this.setAdornments(editor, config.adornments);
            });
        }
        else if (config.path) {
            this.setEditorContent(editor, TFSCodeEditorResources.Loading, false, this.DEFAULT_MONACO_MODE);
            this._versionControlExtension.getItemContents(config.path, config.version, { maxContentLength: this._maxFileContentLength }, (contentInfo) => {
                var currentConfig = this._versionControlExtension.getConfiguration();
                if (currentConfig.path === config.path && currentConfig.version === config.version) {
                    if (contentInfo.exceededMaxContentLength) {
                        this.setErrorMessage(editor, TFSCodeEditorResources.TruncatedEditorContent);
                    }
                    var contentType = this.getContentType(contentInfo);
                    this.setEditorContent(editor, contentInfo.content, true, contentType, { repo: config.repositoryId, version: config.version, path: config.path }, () => {
                        var config = this._versionControlExtension.getConfiguration();
                        this.updateLineNumbersWidth(editor, false, config);
                        this.setDiscussionThreads(editor, config.discussionThreads, config.selectedDiscussionId);
                        this.setAdornments(editor, config.adornments);
                    });
                    this._originalContentType = contentType;
                    this._originalContentPath = contentInfo.path;
                }
            }, (error) => {
                var currentConfig = this._versionControlExtension.getConfiguration();
                if (currentConfig.path === config.path && currentConfig.version === config.version) {
                    this.setErrorMessage(editor, this.getFetchErrorMessage(error));
                    this.setEditorContent(editor, "", true, this.DEFAULT_MONACO_MODE);
                }
            });
        }
        else {
            this.setEditorContent(editor, "", true, this.DEFAULT_MONACO_MODE);
        }

        this._isEditorInitialized = true;
    }

    private updateDiffSideBySide(diffEditor: any, config: any) {
        if (diffEditor && config && !isNullOrUndefined(config.inline)) {
            const renderSideBySide = !config.inline;
            if (preferences.editor.renderSideBySide !== renderSideBySide) {
                preferences.editor.renderSideBySide = renderSideBySide;
                this.updateEditorOptions(diffEditor);
            }
        }
    }

    private showEditDiff(show: boolean) {
        if (show) {
            this._$editorHost.hide();

            if (this._$diffEditorHost) {
                this._$diffEditorHost.show();
            }
        } else {
            this._$editorHost.show();

            if (this._$diffEditorHost) {
                this._$diffEditorHost.hide();
            }
        }
    }

    private renderEditDiff(editor) {
        /// <summary>
        ///    This will be caused when in edit mode and wanting to show a diff from the
        ///    original content
        /// </summary>
        /// <param name="editor" type="Monaco.Editor">
        ///     Current editor used for edit mode
        /// </param>
        this.showEditDiff(true);

        this._diffEditorLeftItemInfo.path = this._originalContentPath || null;
        this._diffEditorLeftItemInfo.version = null;
        this._diffEditorLeftItemInfo.content = this._originalContent;
        this._diffEditorLeftItemInfo.contentType = this._originalContentType || this.DEFAULT_MONACO_MODE;
        this._diffEditorLeftItemInfo.error = null;

        this._diffEditorRightItemInfo.path = this._originalContentPath || null;
        this._diffEditorRightItemInfo.version = null;
        this._diffEditorRightItemInfo.content = editor.getValue();
        this._diffEditorRightItemInfo.contentType = this._originalContentType || this.DEFAULT_MONACO_MODE;
        this._diffEditorRightItemInfo.error = null;

        this.beginGetMonacoMode(this._diffEditorLeftItemInfo.contentType, { filePath: this._diffEditorLeftItemInfo.path }, (rightMode) => {

            this._diffPerformedInitialScroll = false;

            var originalModel = monaco.editor.createModel(this._diffEditorLeftItemInfo.content, rightMode);
            var modifiedModel = monaco.editor.createModel(this._diffEditorRightItemInfo.content, rightMode);
            this.setDiffEditorModels(this._diffEditorForEdit, originalModel, modifiedModel);

            this._diffEditorForEdit.setPosition({
                lineNumber: 1,
                column: 1
            });

            this.updateLineNumbersWidth(this._diffEditorForEdit, true, this._versionControlExtension.getConfiguration());
            this._diffEditorForEdit.layout();
            this._contentLoadInProgress = false;
        });
    }

    private sendCurrentDiffInfo(diffEditor, diffNavigator) {
        /// <summary>
        ///     Send the current diff editor position (next/prev diff enabled/disabled)
        ///     info to the TFS host (based on the position of the cursor)
        /// </summary>
        /// <param name="diffEditor" type="Monaco.Editor">
        ///     Current Diff Editor
        /// </param>
        /// <param name="diffNavigator" type="Object">
        ///     Current diff navigator
        /// </param>
        var prevDiffEnabled = false,
            nextDiffEnabled = false,
            position,
            range;

        if (diffNavigator.ranges && diffNavigator.ranges.length > 0) {
            position = diffEditor.getPosition();

            range = diffNavigator.ranges[0].range;
            if (range && position.lineNumber > range.startLineNumber) {
                prevDiffEnabled = true;
            }

            range = diffNavigator.ranges[diffNavigator.ranges.length - 1].range;
            if (range && position.lineNumber < range.startLineNumber) {
                nextDiffEnabled = true;
            }
        }

        this._versionControlExtension.sendCurrentDiffStatus(prevDiffEnabled, nextDiffEnabled);
    }

    private sendDiffLines(diffNavigator) {
        this._versionControlExtension.sendDiffLines(diffNavigator.ranges.map(rangeObject => rangeObject.range.startLineNumber));
    }

    private fetchDiffItemContent(diffEditor: monaco.editor.IStandaloneDiffEditor,
        currentSideCtx: IDiffEditorItemInfo,
        sidePrefix: string,
        config: any,
        callback: () => void) {
        var sidePath = sidePrefix + "path",
            sideVersion = sidePrefix + "version",
            sideContent = sidePrefix + "content",
            sideContentType = sidePrefix + "contentType";

        if (typeof config[sideContent] === "string") {
            // Content explicitly provided
            currentSideCtx.path = config[sidePath] || null;
            currentSideCtx.version = config[sideVersion] || null;
            currentSideCtx.content = config[sideContent];
            currentSideCtx.contentType = config[sideContentType] || this.DEFAULT_MONACO_MODE;
            currentSideCtx.error = null;
            callback();
        }
        else if (currentSideCtx.path === config[sidePath] && currentSideCtx.version === config[sideVersion]) {
            // Already-cached TFS item content
            callback();
        }
        else if (!config[sidePath]) {
            // No path or content provided - clear data
            currentSideCtx.path = null;
            currentSideCtx.version = null;
            currentSideCtx.content = "";
            currentSideCtx.contentType = this.DEFAULT_MONACO_MODE;
            currentSideCtx.error = null;
            callback();
        }
        else {
            // Fetch the content from TFS
            this._versionControlExtension.getItemContents(config[sidePath], config[sideVersion], { maxContentLength: this._maxFileContentLength }, (contentInfo) => {
                var currentConfig = this._versionControlExtension.getConfiguration();
                if (currentConfig[sidePath] === config[sidePath] && currentConfig[sideVersion] === config[sideVersion]) {
                    currentSideCtx.path = config[sidePath];
                    currentSideCtx.version = config[sideVersion];
                    currentSideCtx.content = contentInfo.content;
                    currentSideCtx.contentType = this.getContentType(contentInfo);
                    currentSideCtx.error = null;

                    if (contentInfo.exceededMaxContentLength) {
                        this.setErrorMessage(diffEditor.getModifiedEditor(), TFSCodeEditorResources.TruncatedDiffMessage);
                    }

                    callback();
                }
            }, (error) => {
                var currentConfig = this._versionControlExtension.getConfiguration();
                if (currentConfig[sidePath] === config[sidePath] && currentConfig[sideVersion] === config[sideVersion]) {
                    currentSideCtx.path = config[sidePath];
                    currentSideCtx.version = config[sideVersion];
                    currentSideCtx.content = this.getFetchErrorMessage(error);
                    currentSideCtx.contentType = this.DEFAULT_MONACO_MODE;
                    currentSideCtx.error = error;

                    callback();
                }
            });
        }
    }

    public setDiffEditorConfig(diffEditor: monaco.editor.IStandaloneDiffEditor, config: any) {
        /// <summary>
        ///     Configures the Monaco diff viewer for communicating with TFS
        /// </summary>
        /// <param name="diffEditor" type="Monaco.Editor">
        ///     Diff Editor to configure
        /// </param>
        /// <param name="config" type="Object">
        ///     Current diff configuration
        /// </param>

        var sameContent = false;
        this.updateUserPreferences(diffEditor, config);
        this.updateCommonEditorConfig(diffEditor, true, config);

        if (config.activeState === false) {
            // The view is now inactive. Remember the config but don't update anything for now.
            return;
        }

        if (config.ocontent && config.mcontent) {
            sameContent =
                (this._diffEditorLeftItemInfo.content === config.ocontent &&
                    this._diffEditorRightItemInfo.content === config.mcontent) ? true : false;
        }
        else if (config.opath && config.mpath) {
            sameContent =
                (this._diffEditorLeftItemInfo.path === config.opath &&
                    this._diffEditorLeftItemInfo.version === config.oversion &&
                    this._diffEditorRightItemInfo.path === config.mpath &&
                    this._diffEditorRightItemInfo.version === config.mversion) ? true : false;
        }
        this.updateDiffSideBySide(diffEditor, config);

        // Set the diff editor mode for discussion manager (if comments are enabled)
        this.setDiffEditorMode(diffEditor, config.inline === true);

        if (sameContent) {
            if (!this._contentLoadInProgress) {
                this.setDiscussionThreads(diffEditor, config.discussionThreads, config.selectedDiscussionId);
                this.setAdornments(diffEditor, config.adornments);
            }
            return;
        }

        this.setContentLoadInProgress(true);
        this.setErrorMessage(diffEditor.getModifiedEditor(), null);
        this.setDiscussionThreads(diffEditor, []);
        this.setAdornments(diffEditor, []);
        this.updateEditorTheme(diffEditor.getModifiedEditor(), config);

        var originalModel = monaco.editor.createModel(TFSCodeEditorResources.Loading, this.DEFAULT_MONACO_MODE);
        var modifiedModel = monaco.editor.createModel(TFSCodeEditorResources.Loading, this.DEFAULT_MONACO_MODE);
        this.setDiffEditorModels(diffEditor, originalModel, modifiedModel);

        this.fetchDiffItemContent(diffEditor, this._diffEditorLeftItemInfo, "o", config, () => {
            this.fetchDiffItemContent(diffEditor, this._diffEditorRightItemInfo, "m", this._versionControlExtension.getConfiguration(), () => {

                if (this._diffEditorRightItemInfo.error) {
                    this.setErrorMessage(diffEditor.getModifiedEditor(), this.getFetchErrorMessage(this._diffEditorRightItemInfo.error));
                }
                else if (this._diffEditorLeftItemInfo.error) {
                    // Check if we're diffing a new file with no "P" previous version.  If so, keep the legacy behavior of showing the same content on both sides.
                    // Example: TF401176: A revision of 'NewFile1.txt' could not be found prior to version <Branch : master> (resolved to commit 'xxx')
                    const PreviousNotFoundGit = "TF401176";
                    const PreviousNotFoundTfvc = "VS403420";
                    const leftItemError = this._diffEditorLeftItemInfo.error;
                    if ((Utils_String.startsWith(leftItemError, PreviousNotFoundGit) || Utils_String.startsWith(leftItemError, PreviousNotFoundTfvc)) &&
                        Utils_String.equals(this._diffEditorLeftItemInfo.version, "P" + this._diffEditorRightItemInfo.version, true)) {
                        this._diffEditorLeftItemInfo.content = this._diffEditorRightItemInfo.content;
                        this._diffEditorLeftItemInfo.contentType = this._diffEditorRightItemInfo.contentType;
                        this._diffEditorLeftItemInfo.error = null;
                    }
                    else {
                        this.setErrorMessage(diffEditor.getModifiedEditor(), this.getFetchErrorMessage(this._diffEditorLeftItemInfo.error));
                    }
                }

                //The monaco editor does not handle diffing different content types, so use the modified file's content type
                this.beginGetMonacoMode(this._diffEditorRightItemInfo.contentType, { filePath: this._diffEditorRightItemInfo.path }, (rightMode) => {

                    this._diffPerformedInitialScroll = false;

                    var originalModel = monaco.editor.createModel(this._diffEditorLeftItemInfo.content, rightMode);
                    var modifiedModel = monaco.editor.createModel(this._diffEditorRightItemInfo.content, rightMode);
                    this.setDiffEditorModels(diffEditor, originalModel, modifiedModel);

                    diffEditor.setPosition({
                        lineNumber: 1,
                        column: 1
                    });

                    this.updateLineNumbersWidth(diffEditor, true, this._versionControlExtension.getConfiguration());
                    diffEditor.layout();
                    this.setContentLoadInProgress(false);
                });
            });
        });

        this._isEditorInitialized = true;
    }

    /**
     * Create a built-in diff editor (Monaco) that can co-exist with other editors within page divs (not iframes).
     * @param callback Callback method which accepts the editor as a parameter.
     * @param versionControlExtension
     */
    public beginCreateBuiltInDiffEditor(callback, versionControlExtension: TFS_Extension.VersionControlExtension) {
        TFSAdvancedCodeEditorPlugin.setModuleCodeEditor(this);
        this.beginCreateDiffEditor(callback, versionControlExtension);
    }

    public beginCreateDiffEditor(callback: (diffEditor: monaco.editor.IDiffEditor) => void, versionControlExtension?: TFS_Extension.VersionControlExtension) {
        /// <summary>
        ///     Ensure the required Monaco modules are loaded, and create a diff editor
        /// </summary>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method which accepts the editor as a parameter.
        /// </param>

        if (!this.checkSupportedVersion()) {
            return;
        }

        const performanceScenario = getScenarioManager().startScenario("VersionControl", "CodeEditor.beginCreateEditor");

        versionControlExtension = versionControlExtension || TFS_Extension.getVersionControlExtension();
        this._versionControlExtension = versionControlExtension;
        this.setHostIdClass();

        this.requireMonaco(() => {
            // If an editor host is set, use it.  Otherwise create a new host
            const $editorHost: JQuery = this._$diffEditorHost || $(".code-editor-host" + this._hostIdClass);

            // No-op if ".code-editor-host" isn't found. Most likely, the DOM was emptied from SPA navigation before the Monaco scripts loaded.
            if (!$editorHost[0]) {
                return;
            }

            const editorOptions = this.getEditorOptions();

            const diffEditor: monaco.editor.IStandaloneDiffEditor = monaco.editor.createDiffEditor($editorHost[0],
                $.extend(editorOptions.configuration || {}, <monaco.editor.IDiffEditorConstructionOptions>{
                    readOnly: editorOptions.editable !== true,
                    renderSideBySide: editorOptions.inline !== true,
                    scrollBeyondLastLine: editorOptions.editable === true,
                    theme: this.getEditorTheme().themeName,
                    fixedOverflowWidgets: true,
                    scrollbar: {
                        horizontalScrollbarSize: 14
                    },
                }));

            preferences.editor.minimap.enabled = preferences.editor.minimap.enabled || editorOptions.minimapEnabled === true;
            this.updateEditorOptions(diffEditor);

            // Add context menu items applicable for all code editor instances
            this.addBuiltInContextMenuItems(diffEditor);

            // add custom context Menu items if any.
            if (editorOptions.contextMenuItems) {
                this.addContextMenuItems(editorOptions.contextMenuItems, diffEditor.getOriginalEditor());
                this.addContextMenuItems(editorOptions.contextMenuItems, diffEditor.getModifiedEditor());
            }

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.EDITOR_ADD_CONTEXT_MENU_ITEM, (data) => {
                if (data.actionData.contextMenuItems) {
                    this.addContextMenuItems(data.actionData.contextMenuItems, diffEditor.getOriginalEditor());
                    this.addContextMenuItems(data.actionData.contextMenuItems, diffEditor.getModifiedEditor());
                }
            });

            $editorHost.data("editor", diffEditor);
            $editorHost.on("remove", () => {
                this._isEditorInitialized = false;
                this.setDiffEditorModels(diffEditor, null, null);
                diffEditor.dispose();
            });

            diffEditor.onDidDispose(() => {
                disposeDisposables(this._diffEditorDisposables);
                this._diffEditorDisposables = [];
            });

            const diffNavigator: monaco.editor.IDiffNavigator = monaco.editor.createDiffNavigator(diffEditor, { alwaysRevealFirst: !editorOptions.selectedDiscussionId });
            this._diffEditorDisposables.push(diffEditor.onDidUpdateDiff(() => {
                this.handleDiffComputed(diffEditor);
                if (!this._diffPerformedInitialScroll) {
                    this._diffPerformedInitialScroll = true;

                    diffEditor.layout();

                    this.sendDiffLines(diffNavigator);
                    this.sendCurrentDiffInfo(diffEditor, diffNavigator);
                }
            }));

            this._diffEditorDisposables.push(diffEditor.getModifiedEditor().onDidChangeCursorPosition((e: monaco.editor.ICursorPositionChangedEvent) => {
                this.sendCurrentDiffInfo(diffEditor, diffNavigator);
            }));

            this._diffEditorDisposables.push(diffEditor.getOriginalEditor().onDidChangeCursorSelection((e: monaco.editor.ICursorSelectionChangedEvent) => {
                this._currentDiffSelection = {
                    originalSide: true,
                    startLineNumber: e.selection.startLineNumber,
                    endLineNumber: e.selection.endLineNumber,
                    startColumn: e.selection.startColumn,
                    endColumn: e.selection.endColumn,
                    positionLineNumber: e.selection.positionLineNumber,
                    positionColumn: e.selection.positionColumn
                };
            }));

            this._diffEditorDisposables.push(diffEditor.getModifiedEditor().onDidChangeCursorSelection((e: monaco.editor.ICursorSelectionChangedEvent) => {
                this._currentDiffSelection = {
                    originalSide: false,
                    startLineNumber: e.selection.startLineNumber,
                    endLineNumber: e.selection.endLineNumber,
                    startColumn: e.selection.startColumn,
                    endColumn: e.selection.endColumn,
                    positionLineNumber: e.selection.positionLineNumber,
                    positionColumn: e.selection.positionColumn
                };
            }));

            this._versionControlExtension.onShowNextDifference(() => {
                diffNavigator.next();
            });

            this._versionControlExtension.onShowPreviousDifference(() => {
                diffNavigator.previous();
            });

            this._versionControlExtension.onNavigateToPosition(e => {
                diffEditor.setPosition(e.actionData);
                diffEditor.revealPositionInCenter(e.actionData);
            });

            if (editorOptions.editable === true) {
                this.addContentChangedListener(diffEditor.getModifiedEditor());
            }

            $(window).resize(() => {
                if (!this._versionControlExtension.getConfiguration().skipLayoutOnResize) {
                    diffEditor.layout();
                }
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.REFRESH_LAYOUT, (message) => {
                diffEditor.layout();
            });

            // Do not hook up these events if in edit mode and showing a diff editor
            if (!this._diffEditMode) {
                this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_EDITOR_CONTENT, (data) => {
                    // Waits to return the editor contents if there is a load in progress.
                    this._contentLoadedDeferred.done(() => {
                        this._versionControlExtension.sendEditorContent(diffEditor.getModifiedEditor().getValue(), data.requestId);
                    });
                });

                this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_SELECTION, (data) => {
                    this._versionControlExtension.sendResponse(data.requestId, TFS_Extension.VersionControl.ActionIds.GET_SELECTION, this._currentDiffSelection);
                });
            }

            diffEditor.getModifiedEditor().onDidChangeConfiguration(() => {
                if (!preferences.ignoreConfigurationChangedEvent) {
                    preferences.ignoreConfigurationChangedEvent = true;
                    diffEditor.getModifiedEditor().updateOptions(this.getAdjustedDiffEditorOptions());
                    preferences.ignoreConfigurationChangedEvent = false;
                }
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.GET_SCROLL_POSITION, (message) => {
                this._versionControlExtension.sendResponse(message.requestId, TFS_Extension.VersionControl.ActionIds.GET_SCROLL_POSITION, this.getEditorScrollPosition(diffEditor.getModifiedEditor()));
            });

            this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.SET_SCROLL_POSITION, (message) => {
                const position: INewScrollPositionWithLine = message.actionData;
                if (position.lineNumber != undefined) {
                    diffEditor.revealRangeAtTop({ startLineNumber: position.lineNumber, endLineNumber: position.lineNumber, startColumn: 1, endColumn: 1 } as monaco.IRange);
                } else {
                    diffEditor.getModifiedEditor().setScrollPosition(position);
                }
            });

            // Notifying completion of editor creation if there are any config that needs to be added after editor creation.
            this._versionControlExtension.sendMessage(TFS_Extension.VersionControl.ActionIds.EDITOR_CREATED);

            if (typeof callback === "function") {
                callback.call(this, diffEditor);
            }

            performanceScenario.end();
        });
    }

    private updateCommonEditorConfig(editor: monaco.editor.IEditor, isDiffEditor: boolean, config: any) {

        const editorId = editor.getId();

        // Update the discussion thread manager
        if (config.commentsEnabled) {
            if (!this._discussionThreadControlManagersByEditorId[editorId]) {
                this._discussionThreadControlManagersByEditorId[editorId] = this.createDiscussionThreadControlManager(<IStandaloneEditor>editor, isDiffEditor);
            }
        }
        else {
            if (this._discussionThreadControlManagersByEditorId[editorId]) {
                this._discussionThreadControlManagersByEditorId[editorId].dispose();
                delete this._discussionThreadControlManagersByEditorId[editorId];
            }
        }

        // Update the line-linking widget
        let lineLinkingWidget = this._lineLinkingWidgetsByEditorId[editorId];
        if (lineLinkingWidget) {
            lineLinkingWidget.dispose();
            delete this._lineLinkingWidgetsByEditorId[editorId];
        }

        if (config.lineLinkingWidgetUrl &&
            editor.getEditorType() === monaco.editor.EditorType.ICodeEditor &&
            (editor as monaco.editor.ICodeEditor).getOption(monaco.editor.EditorOption.readOnly)) {
            this._lineLinkingWidgetsByEditorId[editorId] = new LineLinkingWidget(<monaco.editor.ICodeEditor>editor, config.lineLinkingWidgetUrl);
        }

        // Update the code adornment control manager
        if (config.adornmentsEnabled) {
            if (!this._adornmentControlManagersByEditorId[editorId]) {
                this._adornmentControlManagersByEditorId[editorId] = this.createAdornmentControlManager(editor, isDiffEditor);
            }
        }
        else {
            if (this._adornmentControlManagersByEditorId[editorId]) {
                this._adornmentControlManagersByEditorId[editorId].dispose();
                delete this._adornmentControlManagersByEditorId[editorId];
            }
        }
    }

    private getNumberOfDigits(value: number) {
        var numbersWidth = 0;
        while (value > 0) {
            value = Math.floor(value / 10);
            numbersWidth++;
        }
        return numbersWidth;
    }

    private getNumberOfLinesInEditor(editor: monaco.editor.ICodeEditor): number {
        return editor.getModel().getLineCount();
    }

    private updateLineNumbersWidth(editor: any, isDiffEditor: boolean, config: any) {
        var numbersWidth: number;

        if (isDiffEditor) {
            numbersWidth = Math.max(this.getNumberOfDigits(this.getNumberOfLinesInEditor(editor.originalEditor)), this.getNumberOfDigits(this.getNumberOfLinesInEditor(editor.modifiedEditor)));
        }
        else {
            numbersWidth = this.getNumberOfDigits(this.getNumberOfLinesInEditor(editor));
        }

        if (config.commentsEnabled) {
            numbersWidth += this.NUM_CHARS_FOR_ICON_PADDING;
        }

        numbersWidth = Math.max(numbersWidth, this.DEFAULT_MIN_LINE_CHARS_WIDTH);

        if (numbersWidth !== (isDiffEditor ? editor.modifiedEditor : editor).getOption(monaco.editor.EditorOption.lineNumbersMinChars)) {
            editor.updateOptions({
                lineNumbersMinChars: numbersWidth
            });
        }
    }

    private handleDiffComputed(diffEditor: any) {
        var config: any;
        if (!this._contentLoadInProgress) {
            config = this._versionControlExtension.getConfiguration();
            this.setAdornments(diffEditor, config.adornments);
            return this.setDiscussionThreads(diffEditor, config.discussionThreads, config.selectedDiscussionId);
        }
        return false;
    }

    private setDiscussionThreads(editor: monaco.editor.IEditor, discussionThreads: DiscussionCommon.DiscussionThread[], selectedDiscussionId?: number) {
        var discussionThreadControlManager = this._discussionThreadControlManagersByEditorId[editor.getId()];

        if (discussionThreadControlManager) {
            discussionThreadControlManager.setDiscussionThreads(discussionThreads);
        }

        return false;
    }

    private setDiffEditorMode(editor: monaco.editor.IEditor, inline: boolean): void {
        var discussionThreadControlManager = this._discussionThreadControlManagersByEditorId[editor.getId()];

        if (discussionThreadControlManager) {
            discussionThreadControlManager.setDiffMode(inline);
        }
    }

    private createDiscussionThreadControlManager(editor: IStandaloneEditor, isDiffEditor: boolean) {
        const options = this.getEditorOptions();
        return new DiscussionThreadControlManager(
            options.host.uri,
            editor, isDiffEditor,
            options.supportsDiscussionWorkItemCreation,
            options.supportCommentStatus,
            options.supportsAddComment,
            this._versionControlExtension);
    }

    private setAdornments(editor: monaco.editor.IEditor, adornments: AdornmentCommon.Adornment[]) {
        var adornmentControlManager = this._adornmentControlManagersByEditorId[editor.getId()];

        if (adornmentControlManager) {
            adornmentControlManager.setAdornments(adornments);
        }

        let usingGlyphMargin = adornments && adornments.some(adornment => adornment.usingGlyphMargin);
        editor.updateOptions({ glyphMargin: usingGlyphMargin });
    }

    private createAdornmentControlManager(editor: any, isDiffEditor: boolean) {
        return new AdornmentMonacoUI.MonacoAdornmentControlManager(editor, isDiffEditor);
    }

    public addHighlightingRangeOnHover(selectedRanges: monaco.IRange[], editor: monaco.editor.ICodeEditor) {
        const themeName = this.getEditorTheme().themeName;
        this._highlightableRanges = selectedRanges.map(l => new monaco.Range(l.startLineNumber, l.startColumn, l.endLineNumber, l.endColumn));
        const themedClassName = `${themeName}-highlight`;
        let highlightDecorationId: string[] = [];
        const setHighlightRange = (range?: monaco.Range | null) => {
            if (!range) {
                highlightDecorationId = editor.deltaDecorations(highlightDecorationId, []);
                return;
            }

            highlightDecorationId = editor.deltaDecorations(highlightDecorationId, [{
                range: range,
                options: {
                    className: themedClassName
                }
            }]);
        };

        editor.onMouseMove(e => {
            if (e.target.range) {
                for (let range of this._highlightableRanges) {
                    if (range.intersectRanges(e.target.range)) {
                        return setHighlightRange(range);
                    }
                }
            }
            setHighlightRange(null);
        });
        editor.onMouseLeave(e => setHighlightRange(null));
        editor.onMouseDown(e => setHighlightRange(null));
        editor.onMouseUp(e => setHighlightRange(null));
    };

    public getEditorThemeName(): string {
        return this.getEditorTheme().themeName;
    }
}


/**
 * The original TFSAdvancedCodeEditorPlugin used when hosting the Monaco Code Editor in an iframe.
 * Methods are delegated to a single instance of the CodeEditor class.
 */
export module TFSAdvancedCodeEditorPlugin {

    var _moduleCodeEditor: CodeEditor;

    /**
     * This should only be called from within the CodeEditor to optionally set the module level code editor for testing in cases of only one editor per page.
     */
    export function setModuleCodeEditor(codeEditor: CodeEditor) {
        _moduleCodeEditor = codeEditor;
    }

    export function isContentLoadInProgress() {
        /// <summary>
        ///     Is content currently being loaded by the editor
        /// </summary>
        ///   <returns type="Boolean">
        ///       True if an operation to load content is currently in progress.
        ///   </returns>
        return _moduleCodeEditor.isContentLoadInProgress();
    }

    export function isDiffEditorInitialized() {
        /// <summary>
        ///     Is the diff editor currently initialized and scrolled to the first diff
        /// </summary>
        ///   <returns type="Boolean">
        ///       True if the diff editor is ready/initialized
        ///   </returns>
        return _moduleCodeEditor.isDiffEditorInitialized();
    }

    /**
     * Returns true if the Monaco editor has been initialized.
     * This method was added for functional test synchronization.
     */
    export function isEditorInitialized() {
        return _moduleCodeEditor.isEditorInitialized();
    }

    export function getEditorOptions() {
        /// <summary>
        ///     Get the json options from the request for this extension
        /// </summary>
        ///   <returns type="Object">
        ///       JSON options posted to this extension
        ///   </returns>
        return _moduleCodeEditor.getEditorOptions();
    }

    export function setEditorContent(editor: monaco.editor.ICodeEditor, content: string, clearLoadingFlag: boolean, contentType: string, resource?, callback?) {
        /// <summary>
        ///     Set the content of the editor
        /// </summary>
        /// <param name="editor" type="Monaco.Editor">
        ///     Monaco editor to modify
        /// </param>
        /// <param name="content" type="String">
        ///     File content
        /// </param>
        /// <param name="contentType" type="String">
        ///     File content type (e.g. "application/xml")
        /// </param>
        /// <param name="resource" type="{}">
        ///     The resource of content including path to the file and version.
        /// </param>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method called after the content has been set
        /// </param>
        _moduleCodeEditor.setEditorContent(editor, content, clearLoadingFlag, contentType, resource, callback);
    }

    export function beginCreateEditor(callback) {
        /// <summary>
        ///     Ensure the required Monaco modules are loaded, and create an editor
        /// </summary>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method which accepts the editor as a parameter.
        /// </param>
        _moduleCodeEditor.beginCreateEditor(callback);
    }

    export function setEditorConfig(editor, config) {
        /// <summary>
        ///     Sets the Editor configuration. This makes calls to retrieve the VC item's
        ///     contents and sets the editor content.
        /// </summary>
        /// <param name="editor" type="Monaco.Editor">
        ///     Editor to configure
        /// </param>
        /// <param name="config" type="Object">
        ///     Extension configuration
        /// </param>
        _moduleCodeEditor.setEditorConfig(editor, config);
    }

    export function setDiffEditorConfig(diffEditor, config) {
        /// <summary>
        ///     Configures the Monaco diff viewer for communicating with TFS
        /// </summary>
        /// <param name="diffEditor" type="Monaco.Editor">
        ///     Diff Editor to configure
        /// </param>
        /// <param name="config" type="Object">
        ///     Current diff configuration
        /// </param>
        _moduleCodeEditor.setDiffEditorConfig(diffEditor, config);
    }

    export function beginCreateDiffEditor(callback: (diffEditor: monaco.editor.IDiffEditor) => void) {
        /// <summary>
        ///     Ensure the required Monaco modules are loaded, and create a diff editor
        /// </summary>
        /// <param name="callback" type="IResultCallback">
        ///     Callback method which accepts the editor as a parameter.
        /// </param>

        _moduleCodeEditor.beginCreateDiffEditor(callback);
    }

    _moduleCodeEditor = new CodeEditor();
}


export class ViewZoneDelegate implements monaco.editor.IViewZone {

    public id: string;
    public domNode: HTMLElement;
    public afterLineNumber: number;
    public heightInLines: number;
    private _onDomNodeTop: (top: number) => void;
    private _onComputedHeight: (height: number) => void;

    constructor(domNode: HTMLElement, afterLineNumber: number, heightInLines: number, onDomNodeTop: (top: number) => void, onComputedHeight: (height: number) => void) {
        this.domNode = domNode;
        this.afterLineNumber = afterLineNumber;
        this.heightInLines = heightInLines;
        this._onDomNodeTop = onDomNodeTop;
        this._onComputedHeight = onComputedHeight;
        this.id = "-1";
    }

    public onDomNodeTop(top: number): void {
        this._onDomNodeTop(top);
    }

    public onComputedHeight(height: number): void {
        this._onComputedHeight(height);
    }
}

/**
 * This interface serves to strongly type the use of Monaco's internal vs/editor/contrib/zoneWidget/zoneWidget
 * jaspadie: Future work required to further minimize our dependence on ZoneWidget and/or get it into the Monaco public API.
 */
interface IZoneWidget {

    // Attributes and methods internal to Monaco ZoneWidget
    _viewZone: ViewZoneDelegate;

    associatedFillerWidget: any;
    container: HTMLElement;
    domNode: HTMLElement;
    listenersToRemove: any[];
    rulerDecorations?: any;

    _doLayout: (heightInPixel: number, widthInPixel?: number) => void;
    _fillContainer: (container: HTMLElement) => void;
    _getWidth: (info: monaco.editor.EditorLayoutInfo) => number;
    _onWidth: (widthInPixel: number) => void;
    _onViewZoneTop: (top: number) => void;
    _onViewZoneHeight: (height: number) => void;

    create: () => void;
    dispose: () => void;
    show: (position: monaco.Position, height: number) => void;

    // Custom discussionThread attribute to allow delegation back to parent DiscussionThreadZoneWidget.
    discussionThread: DiscussionThreadZoneWidget;
}

/**
 * Provides the UI of discussions within Monaco by delegation to a IZoneWidget
 */
class DiscussionThreadZoneWidget {

    public referenceKey: string;

    private static extendedZoneWidget = false;
    private static _$measurementElement: JQuery;

    private static DiscussionHeightPaddingPx = 5;
    private static DiscussionRightMarginPx = 12;
    private static DiscussionLeftAdditionalMargin = 10;
    private static EditorZoneWidgetRightMargin = 18;
    private static DiffEditorZoneWidgetRightMargin = 30;
    public static OVERVIEW_RULER_DISCUSSION_COLOR = "rgba(128, 192, 240, 0.5)";

    public isNewControl: boolean;

    private zoneWidget: IZoneWidget;  // extends vs/editor/contrib/zoneWidget/browser/zoneWidget
    private editor: monaco.editor.ICodeEditor;
    private lineNumber: number;
    private isDiffEditor: boolean;
    private created: boolean;
    private hasDiscussionContent: boolean;
    private discussionThreadControls: IMonacoDiscussionThreadControl[];
    private discussionThreadHeightLines: number;
    private isDisposed: boolean;

    private $contents: JQuery;
    private $threadControlContent: JQuery;

    // Options
    private showAbove: boolean;

    constructor(editor: monaco.editor.IEditor, zoneWidget: IZoneWidget, lineInfo: any) {
        this.editor = <monaco.editor.ICodeEditor>editor;
        this.zoneWidget = zoneWidget;
        this.zoneWidget.discussionThread = this;
        this.isDiffEditor = editor.getEditorType() === monaco.editor.EditorType.IDiffEditor;
        this.created = false;
        this.hasDiscussionContent = false;
        this.discussionThreadControls = [];
        this.lineNumber = lineInfo ? lineInfo.line : 1;
        this.showAbove = !lineInfo;
    }

    private _normalizeLineNumber(lineNumber: number) {
        return lineNumber - (this.showAbove ? 1 : 0);
    };

    public showWidget() {
        if (!this.created) {
            this.created = true;
            this.zoneWidget.create();

            this.zoneWidget.rulerDecorations = this.editor.deltaDecorations([], [<monaco.editor.IModelDeltaDecoration>{
                range: {
                    startLineNumber: this.lineNumber,
                    endLineNumber: this.lineNumber,
                    startColumn: 1,
                    endColumn: 1,
                },
                options: {
                    isWholeLine: true,
                    showInOverviewRuler: DiscussionThreadZoneWidget.OVERVIEW_RULER_DISCUSSION_COLOR
                }
            }]);

            this.zoneWidget.show(new monaco.Position(this._normalizeLineNumber(this.lineNumber), 0), this.discussionThreadHeightLines);
        }
        else {
            this.resizeZone(this.lineNumber, this.discussionThreadHeightLines);
        }
    };

    private resizeZone(lineNumber: number, heightInLines: number) {
        const containerHeight = heightInLines * this.editor.getOption(monaco.editor.EditorOption.lineHeight);
        const layoutInfo = this.editor.getLayoutInfo();
        this.zoneWidget.domNode.style.width = this.zoneWidget._getWidth(layoutInfo) + 'px';

        (<any>this).position = {
            lineNumber: lineNumber,
            column: 0
        };

        this.editor.changeViewZones((accessor) => {
            accessor.removeZone(this.zoneWidget._viewZone.id);
            this.zoneWidget._viewZone = new ViewZoneDelegate(document.createElement('div'), this._normalizeLineNumber(lineNumber), heightInLines, (top) => {
                return this.zoneWidget._onViewZoneTop(top);
            }, (height) => {
                return this.zoneWidget._onViewZoneHeight(height);
            });
            this.zoneWidget._viewZone.id = accessor.addZone(this.zoneWidget._viewZone);
        });

        this.zoneWidget.domNode.style.height = containerHeight + 'px';
        this.zoneWidget.container.style.height = containerHeight + 'px';

        this.zoneWidget._doLayout(containerHeight);
    }

    // Implementation of ZoneWidget protected _onWidth(widthInPixel: number): void
    public onWidth(widthInPixel: number): void {
        if (!this.isDisposed) {
            $.each(this.discussionThreadControls, (index: number, threadControl: IMonacoDiscussionThreadControl) => {
                threadControl.updateCommentSizes();
            });
            this.handleResize();
        }
    }

    // Implementation of ZoneWidget protected _doLayout(heightInPixel: number, widthInPixel: number): void
    public doLayout(heightInPixel: number, widthInPixel: number): void {
        this.getPopulatedContents().css("height", heightInPixel + "px");
    }

    // Implementation of ZoneWidget protected _fillContainer(container: HTMLElement): void
    public fillContainer(container: HTMLElement): void {
        this.zoneWidget.container = container;
        var $content = this.getPopulatedContents();
        this.discussionThreadHeightLines = this.calculateLineHeight(this.measureContainerHeight($content));
        $content.appendTo(container);
    }

    public handleResize(lockView?: boolean) {
        const visibleRanges = this.editor.getVisibleRanges();
        const visiblePosition = visibleRanges.length > 0 ? visibleRanges[0].getStartPosition() : null;

        if (!visiblePosition) {
            return;
        }

        const originalScrollHeight = this.editor.getScrollHeight();
        const widgetIsAboveVisibleRange = this.lineNumber < visiblePosition.lineNumber;

        // Recalcuate the required heights in terms of number of lines
        var $contents = this.getPopulatedContents(),
            originalHeight = $contents.outerHeight(),
            newHeightInLines;

        $contents.css("height", "auto");
        newHeightInLines = this.calculateLineHeight($contents.height());

        if (newHeightInLines === this.discussionThreadHeightLines) {
            // No change in # of lines - reset the height back to the explicit height it had previously
            $contents.css("height", originalHeight + "px");
        }
        else {
            this.discussionThreadHeightLines = newHeightInLines;
            this.showWidget();

            // This scroll check adjusts the scrolling when new discussions come in above your view via signalR.
            if (this.isNewControl && lockView && widgetIsAboveVisibleRange) {
                let diff = this.editor.getScrollHeight() - originalScrollHeight;
                if (diff !== 0) {
                    let newPosition = this.editor.getScrollTop() + diff;
                    this.editor.setScrollTop(newPosition);
                }
            }
        }
    }

    private dispose() {
        this.isDisposed = true;

        if (this.zoneWidget.associatedFillerWidget) {
            this.zoneWidget.associatedFillerWidget.dispose();
        }

        // The block below is a workaround for a monaco bug where listeners
        // are not removed on dispose.
        if (this.zoneWidget.listenersToRemove && this.zoneWidget.listenersToRemove.length) {
            this.zoneWidget.listenersToRemove.forEach(function (element) {
                element();
            });
            this.zoneWidget.listenersToRemove = [];
        }

        if (this.zoneWidget.rulerDecorations) {
            this.editor.deltaDecorations(this.zoneWidget.rulerDecorations, []);
            this.zoneWidget.rulerDecorations = null;
        }

        this.zoneWidget.dispose();
    };

    public appendDiscussionContent(threadControl: IMonacoDiscussionThreadControl) {
        this.getPopulatedContents();

        threadControl.appendTo(this.$threadControlContent);
        this.discussionThreadControls.push(threadControl);

        if (!this.hasDiscussionContent) {
            this.hasDiscussionContent = true;
            this.showWidget();
        }
        else {
            this.handleResize();
        }
    };

    public handleDiscussionContentRemoved(threadControlToRemove: IMonacoDiscussionThreadControl) {
        var contentPositionIndex = -1;
        $.each(this.discussionThreadControls, (index: number, threadControl: IMonacoDiscussionThreadControl) => {
            if (threadControl === threadControlToRemove) {
                contentPositionIndex = index;
                this.discussionThreadControls.splice(index, 1);
                return false;
            }
        });
        if (contentPositionIndex === this.discussionThreadControls.length) {
            // Removing the thread at the end. Remove the last spacer.
            contentPositionIndex--;
        }
        if (this.$threadControlContent && contentPositionIndex >= 0) {
            this.handleResize();
        }
    };

    private getPopulatedContents() {
        if (!this.$contents) {

            this.$contents = $("<div />")
                .addClass("monaco-discussion-container")
                .css("margin-left", (this.editor.getLayoutInfo().lineNumbersWidth + DiscussionThreadZoneWidget.DiscussionLeftAdditionalMargin) + "px");

            this.$contents
                .css("margin-right", DiscussionThreadZoneWidget.DiscussionRightMarginPx + "px")
                .addClass("content");

            this.$threadControlContent = $("<div class='discussion-threads-container' />");
            this.$threadControlContent.appendTo(this.$contents);
            this.$threadControlContent.on("discussion-thread-resized discussion-comment-resized", () => {
                this.handleResize();
            });
        }

        return this.$contents;
    };

    private calculateLineHeight(contentHeightInPx) {
        const lineHeight = this.editor.getOption(monaco.editor.EditorOption.lineHeight);

        // if content height is less than a lines height, report height as 0
        const heightWithMargin = (contentHeightInPx > lineHeight && contentHeightInPx + DiscussionThreadZoneWidget.DiscussionHeightPaddingPx) || 0;

        return Math.ceil(heightWithMargin / lineHeight);
    };

    private measureContainerHeight($container) {
        var zoneWidgetWidth;
        if (!DiscussionThreadZoneWidget._$measurementElement) {
            DiscussionThreadZoneWidget._$measurementElement = $("<div />")
                .addClass("discussion-measurement-probe")
                .css("position", "absolute")
                .css("top", "-99999px")
                .css("left", "-99999px")
                .appendTo(document.body);
        }

        zoneWidgetWidth = this.editor.getLayoutInfo().width;
        if (this.isDiffEditor) {
            zoneWidgetWidth -= DiscussionThreadZoneWidget.DiffEditorZoneWidgetRightMargin;
        }
        else {
            zoneWidgetWidth -= DiscussionThreadZoneWidget.EditorZoneWidgetRightMargin;
        }

        DiscussionThreadZoneWidget._$measurementElement.css("width", zoneWidgetWidth + "px");
        $container.appendTo(DiscussionThreadZoneWidget._$measurementElement);

        $.each(this.discussionThreadControls, (index: number, threadControl: IMonacoDiscussionThreadControl) => {
            threadControl.updateCommentSizes();
        });

        return $container.height();
    };

    public scrollIntoView() {
        this.editor.revealPositionInCenter({
            lineNumber: this.lineNumber,
            column: 1
        });
    };
}

// The Monaco zoneWidget abstract class is not exposed in monaco.d.ts,
// and it is one of the last places where we are still reaching into Monaco internals to inherit from it.
// The inheritance use is minimized by delegating to/from the discussionThread instance that contains it.
// Future work should be done to remove the dependence entirely, or add the necessarily exposure in monaco.d.ts.
var _zoneWidgetType: any = null;
function beginGetZoneWidgetType(callback: (zoneWidgetType: IZoneWidget) => void) {

    if (_zoneWidgetType) {
        callback.call(this, _zoneWidgetType);
    }
    else {
        const zoneWidget = (window as any).LWL.req("monaco-editor").ZoneWidget;
        _zoneWidgetType = (function () {

            var ZoneWidgetType = function (editor: monaco.editor.IEditor) {

                zoneWidget.call(this, editor, {
                    showFrame: false,
                    showArrow: false
                });
            }

            var __extends = this.__extends || function (d, b) {
                function __() { this.constructor = d; }
                __.prototype = b.prototype;
                d.prototype = new __();
            }
            __extends(ZoneWidgetType, zoneWidget);

            // Implementation of ZoneWidget protected _onWidth(widthInPixel: number): void
            ZoneWidgetType.prototype._onWidth = function (widthInPixel: number): void {
                this.discussionThread.onWidth(widthInPixel);
            };

            // Implementation of ZoneWidget protected _doLayout(heightInPixel: number, widthInPixel: number): void
            ZoneWidgetType.prototype._doLayout = function (heightInPixel: number, widthInPixel: number): void {
                this.discussionThread.doLayout(heightInPixel, widthInPixel);
            };

            // Implementation of protected abstract _fillContainer(container: HTMLElement): void
            ZoneWidgetType.prototype._fillContainer = function (container: HTMLElement): void {
                this.discussionThread.fillContainer(container);
            };

            return ZoneWidgetType;
        })();

        callback.call(this, _zoneWidgetType);
    }
}

/**
 * A widget that allows copying the URL for the specific file/selection to the clipboard.
 */
class LineLinkingWidget implements monaco.editor.IContentWidget {

    private _editor: monaco.editor.ICodeEditor;
    private _originalUrl: string;
    private _selection: monaco.Selection;
    private _disposables: monaco.IDisposable[] = [];
    private _delayedUpdate: Utils_Core.DelayedFunction = null;
    private _$domNode: JQuery;

    constructor(editor: monaco.editor.ICodeEditor, originalUrl: string) {
        this._editor = editor;
        this._originalUrl = originalUrl;

        this._editor.addContentWidget(this);
        this._disposables.push(this._editor.onDidChangeCursorSelection((e: monaco.editor.ICursorSelectionChangedEvent) => {
            this.setSelection(null);
            if (!isEmptySelection(e.selection)) {
                this.setSelection(e.selection);
            }
        }));
    };

    public getId(): string {
        return this._editor.getId() + ":LineLinkingWidget";
    }

    /**
     * The link node includes a tooltip with the dynamic "Copied!" message and a delay similar to Office Fabric tooltips.
     */
    public getDomNode(): HTMLElement {
        if (!this._$domNode) {
            this._$domNode = $('<div class="get-line-link" />');
            const toolTip = RichContentTooltip.add(TFSCodeEditorResources.CopyLineLinkTitle, this._$domNode, { openDelay: 300 });
            this._$domNode
                .click(e => {
                    this.copyLineLink();
                    toolTip.setTextContent(TFSCodeEditorResources.CopiedLineLinkTitle);
                    setTimeout(() => toolTip.show(), 0);
                    return false;
                })
                .mouseout(e => {
                    toolTip.setTextContent(TFSCodeEditorResources.CopyLineLinkTitle);
                });

            $('<div class="bowtie-icon bowtie-link" />').appendTo(this._$domNode);
        }
        return this._$domNode[0];
    }

    /**
     * Get the placement of the content widget.
     * If null is returned, the content widget will be placed off screen.
     */
    public getPosition(): monaco.editor.IContentWidgetPosition {

        if (this._selection) {
            var positionPreference: monaco.editor.ContentWidgetPositionPreference[];

            if (isUpwardSelection(this._selection)) {
                positionPreference = [monaco.editor.ContentWidgetPositionPreference.ABOVE, monaco.editor.ContentWidgetPositionPreference.BELOW];
            }
            else {
                positionPreference = [monaco.editor.ContentWidgetPositionPreference.BELOW, monaco.editor.ContentWidgetPositionPreference.ABOVE];
            }

            return <monaco.editor.IContentWidgetPosition>{
                position: {
                    column: this._selection.positionColumn,
                    lineNumber: this._selection.positionLineNumber
                },
                preference: positionPreference
            };
        }
        else {
            return null;
        }
    }

    private setSelection(selection: monaco.Selection) {
        this._selection = selection;

        if (this._delayedUpdate) {
            this._delayedUpdate.cancel();
        }

        $(this.getDomNode()).hide();

        if (selection) {
            this._delayedUpdate = Utils_Core.delay(this, 250, () => {
                $(this.getDomNode()).show();
                this._editor.layoutContentWidget(this);
                this._delayedUpdate = null;
            });
        }
    }

    private copyLineLink() {
        let newUrl = Utils_Url.Uri.parse(this._originalUrl);

        if (this._selection) {
            newUrl.addQueryParam("line", this._selection.startLineNumber + "", true);
            newUrl.addQueryParam("lineStyle", "plain", true);
            newUrl.addQueryParam("lineEnd", this._selection.endLineNumber + "", true);
            newUrl.addQueryParam("lineStartColumn", this._selection.startColumn + "", true);
            newUrl.addQueryParam("lineEndColumn", this._selection.endColumn + "", true);
        }

        Utils_Clipboard.copyToClipboard(newUrl.absoluteUri);
    }

    public dispose() {
        this._editor.removeContentWidget(this);
        if (this._disposables) {
            for (let disposable of this._disposables) {
                disposable.dispose();
            }
            this._disposables = [];
        }
    }
}

/**
 * A widget that shows in the left-side gutter for adding a comment on the line.
 */
class AddCommentGutterWidget implements monaco.editor.IOverlayWidget {

    private _discussionManager: DiscussionThreadControlManager;
    private _$domNode: JQuery = null;
    private _isOriginalEditor: boolean;
    private _lineNumber: number = 0;

    constructor(discussionManager: DiscussionThreadControlManager, isOriginalEditor: boolean) {
        this._discussionManager = discussionManager;
        this._isOriginalEditor = isOriginalEditor;
    }

    public getId() {
        return "add-comment-gutter";
    }

    public getDomNode() {
        if (!this._$domNode) {
            this._$domNode = $("<div />")
                .attr("title", DiscussionResources.AddCommentTooltip)
                .addClass("add-comment-gutter add-comment-background-icon")
                .click(() => {
                    this._discussionManager.createNewThread(this._discussionManager.getDiscussionPosition(this._lineNumber, this._isOriginalEditor));
                    return false;
                });
        }
        return this._$domNode[0];
    }

    public getPosition() {
        return null;
    }

    public setPosition(lineNumber: number, topOffset: number) {
        this._lineNumber = lineNumber;
        this.getDomNode().style.top = topOffset + "px";
    }

    public hide() {
        this.setPosition(0, -1000);
    }
}

/**
 * A widget that shows on the selected text for adding a comment for that selection.
 */
class AddCommentSelectionWidget implements monaco.editor.IContentWidget {

    private _discussionManager: DiscussionThreadControlManager;
    private _delayedUpdate: Utils_Core.DelayedFunction = null;
    private _editor: monaco.editor.ICodeEditor;
    private _$domNode: JQuery = null;
    private _isOriginalEditor: boolean;
    private _selection: monaco.Selection;

    public allowEditorOverflow: boolean = true;

    constructor(editor: monaco.editor.ICodeEditor, discussionManager: DiscussionThreadControlManager, isOriginalEditor: boolean) {
        this._editor = editor;
        this._discussionManager = discussionManager;
        this._isOriginalEditor = isOriginalEditor;
    }

    public getId() {
        return "add-comment-selection";
    }

    public getDomNode() {

        if (!this._$domNode) {
            this._$domNode = $('<div class="add-comment-selection" />')
                .attr("title", DiscussionResources.SelectionCommentTooltip)
                .click(() => {
                    this._discussionManager.createNewThread(this._discussionManager.getDiscussionPositionFromSelection(this._selection, this._isOriginalEditor));
                    this.hide();
                    return false;
                });

            $('<div class="add-comment-background-icon" />').appendTo(this._$domNode);
        }
        return this._$domNode[0];
    }

    public getPosition(): monaco.editor.IContentWidgetPosition {
        if (this._selection) {
            var positionPreference: any;

            if (isUpwardSelection(this._selection)) {
                positionPreference = [monaco.editor.ContentWidgetPositionPreference.ABOVE, monaco.editor.ContentWidgetPositionPreference.BELOW];
            }
            else {
                positionPreference = [monaco.editor.ContentWidgetPositionPreference.BELOW, monaco.editor.ContentWidgetPositionPreference.ABOVE];
            }
            return <monaco.editor.IContentWidgetPosition>{
                position: {
                    lineNumber: this._selection.positionLineNumber,
                    column: this._selection.positionColumn
                },
                preference: positionPreference
            };
        }
        else {
            return null;
        }
    }

    public setSelection(selection: monaco.Selection) {
        this._selection = selection;

        if (this._delayedUpdate) {
            this._delayedUpdate.cancel();
        }

        $(this.getDomNode()).hide();

        if (selection) {
            this._delayedUpdate = Utils_Core.delay(this, 500, () => {
                $(this.getDomNode()).show();
                this._editor.layoutContentWidget(this);
                this._delayedUpdate = null;
            });
        }
    }

    public hide() {
        this.setSelection(null);
    }
}


class DiscussionThreadControlManager extends DiscussionCommonUI.DiscussionThreadControlManagerBase {

    private _versionControlExtension: TFS_Extension.VersionControlExtension;
    private _hostUrl: string;
    private _editor: IStandaloneEditor;
    private _isDiffEditor: boolean;
    private _supportsCreateWorkItem: boolean;
    private _supportsAddComment: boolean;
    private _zoneWidgetsByKey: any;
    private _zoneWidgetRefCounts: any;
    private _disposables: monaco.IDisposable[];
    private _extensionMessageListener: any;

    private _lastMouseOverLine: number;
    private _lastMouseOverIsOriginalEditor: boolean;
    private _originalEditorGutterWidget: any;
    private _modifiedEditorGutterWidget: any;
    private _lastMouseOverDecorations: any[];

    private _originalEditorSelectionWidget: any;
    private _modifiedEditorSelectionWidget: any;
    private _diffInline: boolean;

    private _currentlySelectedThreadId: number;

    private _supportCommentStatus: boolean
    private _discussionThreadControlsByUniqueId: IDictionaryStringTo<MonacoDiscussionThreadControl>;

    constructor(
        hostUrl: string,
        editor: IStandaloneEditor,
        isDiffEditor: boolean,
        supportsCreateWorkItem: boolean,
        supportCommentStatus: boolean,
        supportsAddComment: boolean,
        versionControlExtension: TFS_Extension.VersionControlExtension) {

        super();

        this._hostUrl = hostUrl;
        this._editor = editor;
        this._isDiffEditor = isDiffEditor;
        this._supportsCreateWorkItem = supportsCreateWorkItem;
        this._supportCommentStatus = supportCommentStatus;
        this._supportsAddComment = supportsAddComment;
        this._versionControlExtension = versionControlExtension;
        this._diffInline = false;
        this._zoneWidgetsByKey = {};
        this._zoneWidgetRefCounts = {};
        this._disposables = [];

        if (isDiffEditor) {
            this.addDiscussionWidgetsAndCommands((<monaco.editor.IStandaloneDiffEditor>editor).getOriginalEditor(), true);
            this.addDiscussionWidgetsAndCommands((<monaco.editor.IStandaloneDiffEditor>editor).getModifiedEditor(), false);
        }
        else {
            this.addDiscussionWidgetsAndCommands(<monaco.editor.IStandaloneCodeEditor>editor, false);
        }

        this._extensionMessageListener = (data: any) => {
            this.handleExtensionThreadUpdateEvent(data.actionData || {});
        };

        this._versionControlExtension.addMessageListener(TFS_Extension.VersionControl.ActionIds.DISCUSSION_THREAD_UPDATED, this._extensionMessageListener);
    }

    public dispose() {
        this.clearDiscussionThreads();
        this._discussionThreadControlsByUniqueId = {};

        if (this._extensionMessageListener) {
            this._versionControlExtension.removeMessageListener(this._extensionMessageListener);
        }

        disposeDisposables(this._disposables);
        this._disposables = [];

        if (this._lastMouseOverDecorations) {
            this.getEditor(this._lastMouseOverIsOriginalEditor).deltaDecorations(this._lastMouseOverDecorations, []);
            this._lastMouseOverDecorations = null;
        }

        if (this._originalEditorGutterWidget) {
            (<monaco.editor.ICodeEditor>this.getEditor(true)).removeOverlayWidget(this._originalEditorGutterWidget);
            this._originalEditorGutterWidget = null;
        }
        else if (this._modifiedEditorGutterWidget) {
            (<monaco.editor.ICodeEditor>this.getEditor(false)).removeOverlayWidget(this._modifiedEditorGutterWidget);
            this._modifiedEditorGutterWidget = null;
        }

        if (this._originalEditorSelectionWidget) {
            (<monaco.editor.ICodeEditor>this.getEditor(true)).removeContentWidget(this._originalEditorSelectionWidget);
            this._originalEditorSelectionWidget = null
        }
        if (this._modifiedEditorSelectionWidget) {
            (<monaco.editor.ICodeEditor>this.getEditor(false)).removeContentWidget(this._modifiedEditorSelectionWidget);
            this._modifiedEditorSelectionWidget = null;
        }
    }

    public setDiffMode(inline: boolean): void {
        this._diffInline = inline;
    }

    public showCreateWorkItemDropdown(): boolean {
        return this._supportsCreateWorkItem;
    }

    public beginGetWorkItemTypeNames(callback: (workItemTypeNames: string[]) => void) {
        this._versionControlExtension.sendMessage(TFS_Extension.Discussion.ActionIds.GET_WORK_ITEM_TYPES, {}, (message) => {
            callback.call(this, message.actionData.workItemTypeNames);
        });
    }

    public getVersionControlExtension() {
        return this._versionControlExtension;
    }

    private handleExtensionThreadUpdateEvent(eventData: DiscussionCommon.DiscussionThreadsUpdateEvent) {
        var config = this._versionControlExtension.getConfiguration();

        if (this.isContentLoadInProgress()) {
            if (eventData.currentThreads) {
                config.discussionThreads = eventData.currentThreads;
            }
            else {
                if (eventData.newThreads) {
                    let currentThreads = <DiscussionCommon.DiscussionThread[]>(config.discussionThreads || []);
                    let newThreads = this.filterThreads(eventData.newThreads, currentThreads);
                    config.discussionThreads = currentThreads.concat(newThreads);
                }
            }
            if (eventData.threadSelected) {
                config.selectedDiscussionId = eventData.threadSelected.id;
            }
        }
        else {
            if (eventData.currentThreads) {
                this.setDiscussionThreads(eventData.currentThreads);
            }
            else {
                if (eventData.newThreads) {
                    let currentThreads = <DiscussionCommon.DiscussionThread[]>(config.discussionThreads || []);
                    let newThreads = this.filterThreads(eventData.newThreads, currentThreads);
                    $.each(newThreads, (index, newThread) => {
                        this.createFileThread(newThread, index === 0, eventData.createdByUser);
                    });
                }
            }
        }
    }

    /**
     * Dedupe any new threads with existing ones.
     */
    private filterThreads(newThreads: DiscussionCommon.DiscussionThread[], currentThreads: DiscussionCommon.DiscussionThread[]): DiscussionCommon.DiscussionThread[] {
        return newThreads.filter((newThread: DiscussionCommon.DiscussionThread) => {
            return !Utils_Array.arrayContains(newThread, currentThreads, (thread1, thread2) => {
                let isDuplicate = thread1.id === thread2.id;
                // avoid any race condition by ensuring we don't add a new thread that already exists.
                if (isDuplicate && Diag.getDebugMode() && window.console && window.console.log) {
                    console.log("Duplicate thread id: " + thread1.id);
                }
                return isDuplicate;
            });
        });
    }

    private addDiscussionWidgetsAndCommands(editor: monaco.editor.IStandaloneCodeEditor, isOriginalEditor: boolean) {
        if (this._supportsAddComment) {
            this.addGutterWidget(editor, isOriginalEditor);
            this.addSelectionWidget(editor, isOriginalEditor);
            this.addCommentCommand(editor, isOriginalEditor);
        }
    }

    /**
     * Add a command to the editor context menu (with a key binding).
     * See: https://microsoft.github.io/monaco-editor/playground.html#interacting-with-the-editor-adding-an-action-to-an-editor-instance
     */
    private addCommentCommand(editor: monaco.editor.IStandaloneCodeEditor, isOriginalEditor: boolean) {
        let discussionManager = this;

        editor.addAction({
            // unique identifier of the contributed action.
            id: 'add-comment',
            label: DiscussionResources.AddCommentEditorCommand,
            // An optional array of keybindings for the action.
            keybindings: [monaco.KeyCode.F4],
            keybindingContext: null,
            contextMenuGroupId: '1_modification',
            contextMenuOrder: 10,
            run: (ed: monaco.editor.ICodeEditor) => {
                discussionManager.createNewThread(discussionManager.getDiscussionPosition(ed.getPosition().lineNumber, isOriginalEditor));
                return null;
            }
        });
    }

    private addGutterWidget(editor: monaco.editor.ICodeEditor, isOriginalEditor: boolean) {

        const addCommentGutterWidget = new AddCommentGutterWidget(this, isOriginalEditor);
        editor.addOverlayWidget(addCommentGutterWidget);

        if (isOriginalEditor) {
            this._originalEditorGutterWidget = addCommentGutterWidget;
        }
        else {
            this._modifiedEditorGutterWidget = addCommentGutterWidget;
        }

        this._disposables.push(editor.onMouseMove((e: monaco.editor.IEditorMouseEvent) => {

            let position = e.target.position;

            // In case of CodeCoverage Adornment after adding tooltip, position value is not getting populated and hence comment bubble is
            // not appearing along with Tooltip. Hence, setting the position explicitly.
            if (e.target.element && e.target.element.className === "gutter-adornment-tooltip-host-childnode") {
                position = editor.getTargetAtClientPoint((e.event as any).editorPos.x, e.event.posy).position;
            }

            let lineNumber = position ? position.lineNumber : 0;
            let newDecorations: monaco.editor.IModelDeltaDecoration[];

            if (lineNumber === this._lastMouseOverLine && isOriginalEditor === this._lastMouseOverIsOriginalEditor) {
                // At the same position as last
                return;
            }

            if (e.target.detail === "add-comment-gutter") {
                // hovered over the gutter widget
                return;
            }

            if (this._isDiffEditor) {
                if (isOriginalEditor) {
                    this._modifiedEditorGutterWidget.hide();
                }
                else {
                    this._originalEditorGutterWidget.hide();
                }
            }

            if (!position || this.isContentLoadInProgress()) {
                // No position selected or loading file - clear the line highlight
                addCommentGutterWidget.hide();
                newDecorations = [];
            }
            else {
                addCommentGutterWidget.setPosition(lineNumber, editor.getTopForLineNumber(lineNumber) - editor.getScrollTop());

                newDecorations = [<monaco.editor.IModelDecoration>{
                    range: {
                        startLineNumber: lineNumber,
                        endLineNumber: lineNumber
                    },
                    options: {
                        isWholeLine: true,
                        className: "highlighted-line"
                    }
                }];
            }

            if (this._isDiffEditor && isOriginalEditor !== this._lastMouseOverIsOriginalEditor && this._lastMouseOverDecorations) {
                this.getEditor(!isOriginalEditor).deltaDecorations(this._lastMouseOverDecorations, []);
                this._lastMouseOverDecorations = editor.deltaDecorations([], newDecorations);
            }
            else {
                this._lastMouseOverDecorations = editor.deltaDecorations(this._lastMouseOverDecorations || [], newDecorations);
            }

            this._lastMouseOverIsOriginalEditor = isOriginalEditor;
            this._lastMouseOverLine = lineNumber;
        }));
    }

    // To support the old legacy iframe extension, we have to check TFSAdvancedCodeEditorPlugin instead.
    private isContentLoadInProgress(): boolean {
        var vcLoadInProgress = this._versionControlExtension.isContentLoadInProgress();
        return (vcLoadInProgress !== null) ? vcLoadInProgress : TFSAdvancedCodeEditorPlugin.isContentLoadInProgress();
    }

    private addSelectionWidget(editor: monaco.editor.ICodeEditor, isOriginalEditor: boolean) {

        let addCommentSelectionWidget = new AddCommentSelectionWidget(editor, this, isOriginalEditor);
        editor.addContentWidget(addCommentSelectionWidget);

        if (isOriginalEditor) {
            this._originalEditorSelectionWidget = addCommentSelectionWidget;
        }
        else {
            this._modifiedEditorSelectionWidget = addCommentSelectionWidget;
        }

        this._disposables.push(editor.onDidChangeCursorSelection((e: monaco.editor.ICursorSelectionChangedEvent) => {

            if (this._isDiffEditor) {
                if (isOriginalEditor) {
                    this._modifiedEditorSelectionWidget.hide();
                }
                else {
                    this._originalEditorSelectionWidget.hide();
                }
            }

            if (isEmptySelection(e.selection)) {
                addCommentSelectionWidget.hide();
            }
            else {
                addCommentSelectionWidget.setSelection(e.selection);
            }
        }));
    }

    public getEditor(originalSide: boolean): monaco.editor.ICodeEditor {
        if (this._isDiffEditor) {
            let diffEditor = <monaco.editor.IDiffEditor>this._editor;
            return originalSide ? diffEditor.getOriginalEditor() : diffEditor.getModifiedEditor();
        }
        else {
            return <monaco.editor.ICodeEditor>this._editor;
        }
    }

    public getDiscussionPosition(lineNumber: number, isOriginalEditor: boolean) {

        var editor = this.getEditor(isOriginalEditor),
            selection = editor.getSelection();

        if (!isEmptySelection(selection) &&
            lineNumber >= selection.startLineNumber &&
            lineNumber <= selection.endLineNumber) {

            return this.getDiscussionPositionFromSelection(selection, isOriginalEditor);
        }
        else {
            return <DiscussionCommon.DiscussionPosition>{
                positionContext: this.getDiscussionPositionContext(isOriginalEditor),
                startLine: lineNumber,
                endLine: lineNumber,
                startColumn: 1,
                endColumn: (editor.getModel().getLineContent(lineNumber) || "").length + 1
            };
        }
    }

    public getDiscussionPositionFromSelection(selection: monaco.Selection, isOriginalEditor: boolean) {
        return <DiscussionCommon.DiscussionPosition>{
            positionContext: this.getDiscussionPositionContext(isOriginalEditor),
            startLine: selection.startLineNumber,
            endLine: selection.endLineNumber,
            startColumn: selection.startColumn,
            endColumn: selection.endColumn
        };
    }

    private getDiscussionPositionContext(isOriginalEditor: boolean) {
        if (this._isDiffEditor) {
            return isOriginalEditor ? DiscussionCommon.PositionContext.LeftBuffer : DiscussionCommon.PositionContext.RightBuffer;
        }
        else {
            return this._versionControlExtension.getConfiguration().commentsDefaultPositionContext || DiscussionCommon.PositionContext.RightBuffer;
        }
    }

    private processDiscussionThreads(discussionThreads: DiscussionCommon.DiscussionThread[]) {
        $.each(discussionThreads || [], (threadIndex: number, discussionThread: DiscussionCommon.DiscussionThread) => {
            $.each(discussionThread.comments || [], (commentIndex: number, comment: DiscussionCommon.DiscussionComment) => {
                this.processDiscussionComment(comment);
            });
        });
        return discussionThreads || [];
    }

    private processDiscussionComment(discussionComment: DiscussionCommon.DiscussionComment) {
        if (discussionComment.publishedDate && !(discussionComment.publishedDate instanceof Date)) {
            discussionComment.publishedDate = new Date(<any>discussionComment.publishedDate);
        }
        if (discussionComment.lastUpdatedDate && !(discussionComment.lastUpdatedDate instanceof Date)) {
            discussionComment.lastUpdatedDate = new Date(<any>discussionComment.lastUpdatedDate);
        }
        return discussionComment;
    }

    public setDiscussionThreads(discussionThreads: DiscussionCommon.DiscussionThread[]) {
        this.redraw(this.processDiscussionThreads(discussionThreads));
    }

    private redraw(discussionThreads: DiscussionCommon.DiscussionThread[]) {

        // For now, we are redrawing everything, so clear comments again to ensure
        // that comments are not added twice due to multiple ajax requests outstanding
        // at the same time. Eventually we should be smarter here and only update what changed.
        this.clearDiscussionThreads();
        this._discussionThreadControlsByUniqueId = {};

        $.each(discussionThreads, (i: number, discussionThread: DiscussionCommon.DiscussionThread) => {
            this.createFileThread(discussionThread);
        });
    }

    public getIdentityImageUrl(userId: string) {
        return this._hostUrl + "/_api/_common/IdentityImage?" + $.param({
            id: userId
        });
    }

    public createNewThread(
        position: DiscussionCommon.DiscussionPosition,
        callback?: (newThread: DiscussionCommon.DiscussionThread) => void) {
        this._versionControlExtension.sendMessage(TFS_Extension.Discussion.ActionIds.CREATE_THREAD, {
            position: position
        }, function (message) {
            if (typeof callback === "function") {
                callback.call(this, message.actionData);
            }
        });
    }

    public createFileThread(thread: DiscussionCommon.DiscussionThread, focus?: boolean, createdByUser?: boolean) {
        var threadControl: IMonacoDiscussionThreadControl,
            lineNumber = 0,
            isOriginalSide = false,
            lastLineText: string;

        this._discussionThreadControlsByUniqueId = this._discussionThreadControlsByUniqueId || {};

        // if there's a unique id for this thread, make sure we don't render a second one
        if (thread.uniqueId && this._discussionThreadControlsByUniqueId[thread.uniqueId]) {
            let prevThread = this._discussionThreadControlsByUniqueId[thread.uniqueId].getThread();
            Diag.logError(Utils_String.format("thread control was already registered. ID: {0}={1}, UniqueID: {2}={3}, OriginalId: {4}={5}",
                thread.id, prevThread && prevThread.id,
                thread.uniqueId, prevThread && prevThread.uniqueId,
                thread.originalId, prevThread && prevThread.originalId));

            // don't go on to create a duplicate thread control if this happens
            return;
        }

        if (thread.position && thread.position.positionContext !== DiscussionCommon.PositionContext.InLineBuffer) {
            if (this._isDiffEditor && thread.position.positionContext) {
                isOriginalSide = thread.position.positionContext === DiscussionCommon.PositionContext.LeftBuffer;
            }
            lineNumber = (thread.position.endColumn === 1 && thread.position.endLine > thread.position.startLine) ? thread.position.endLine - 1 : thread.position.endLine;

            const model = this.getEditor(isOriginalSide).getModel();
            if (lineNumber > 0 && lineNumber <= model.getLineCount()) {
                lastLineText = model.getLineContent(lineNumber);
            }
        }

        let widgetIsAboveVisibleRange: boolean;
        let originalScrollHeight: number;

        const editor = this.getEditor(isOriginalSide);

        if (editor) {
            const visibleRanges = editor.getVisibleRanges();
            const visiblePosition = visibleRanges.length > 0 ? visibleRanges[0].getStartPosition() : null;

            if (visiblePosition) {
                originalScrollHeight = editor.getScrollHeight();
                widgetIsAboveVisibleRange = !thread.position || thread.position.endLine < visiblePosition.lineNumber;
            }
        }

        threadControl = new MonacoDiscussionThreadControl(thread, this, isOriginalSide);
        this.registerThreadControl(threadControl);
        this._discussionThreadControlsByUniqueId[thread.uniqueId] = threadControl as MonacoDiscussionThreadControl;

        this.beginGetZoneWidget(lineNumber, isOriginalSide, (discussionThreadZoneWidget: DiscussionThreadZoneWidget) => {
            this._zoneWidgetRefCounts[discussionThreadZoneWidget.referenceKey]++;

            const currentView = editor && editor.saveViewState();
            threadControl.setZoneWidget(discussionThreadZoneWidget);

            if (!createdByUser && currentView) {
                // when adding a new zone, monaco wants to jump to that position so restore the previous view state
                editor.restoreViewState(currentView);
            }
        });

        if (widgetIsAboveVisibleRange && !createdByUser && editor) {
            // adjust the scroll based on the size, should only happen if the new thread came in above where we were
            const diff = editor.getScrollHeight() - originalScrollHeight;
            diff && editor.setScrollTop(editor.getScrollTop() + diff);
        }
    }

    private beginGetZoneWidget(line: number, isOriginalSide: boolean, callback?: (zoneWidget: any) => void) {
        var key = (isOriginalSide ? "o" : "m") + line,
            widget = this._zoneWidgetsByKey[key];

        if (widget) {
            if (typeof callback === "function") {
                callback.call(this, widget);
            }
        }
        else {
            VSS.queueRequest(this, this, "_widgetTypeInfo", (zoneWidgetTypeInfo: any) => {
                widget = this._zoneWidgetsByKey[key];
                if (!widget) {
                    widget = this._createDiscussionThreadWidget(zoneWidgetTypeInfo.widgetType, line, isOriginalSide);
                    widget.referenceKey = key;
                    this._zoneWidgetsByKey[key] = widget;
                    this._zoneWidgetRefCounts[key] = 0;
                }
                if (typeof callback === "function") {
                    callback.call(this, widget);
                }
            }, null, (succeeded, failed) => {
                beginGetZoneWidgetType((zoneWidgetType: any) => {
                    succeeded({
                        widgetType: zoneWidgetType
                    });
                });
            });
        }
    }

    public _releaseWidgetReference(widget: any) {
        if (--this._zoneWidgetRefCounts[widget.referenceKey] < 1) {
            delete this._zoneWidgetRefCounts[widget.referenceKey];
            delete this._zoneWidgetsByKey[widget.referenceKey];
            widget.dispose();
        }
    }

    private _createDiscussionThreadWidget(widgetType: any, line: number, isOriginalSide: boolean): DiscussionThreadZoneWidget {
        let editor: monaco.editor.IEditor = this._editor;
        let lineInfo: any = null;

        if (line > 0) {
            lineInfo = {
                line: line
            };
        }

        if (this._isDiffEditor) {
            const diffEditor = <monaco.editor.IDiffEditor>this._editor;
            if (this._diffInline) {
                // If inline diff, comments will always be rendered on modified side
                // For this reason, we need to first figure out correct line number on modified side
                if (isOriginalSide && lineInfo) {
                    lineInfo.line = diffEditor.getDiffLineInformationForOriginal(lineInfo.line).equivalentLineNumber;
                }

                // Set editor to modifiedEditor
                editor = diffEditor.getModifiedEditor();
            } else {
                // Set editor accoirding to the side when side by side rendered
                editor = isOriginalSide ? diffEditor.getOriginalEditor() : diffEditor.getModifiedEditor();
            }
        }

        const zoneWidget = new widgetType(editor);
        return new DiscussionThreadZoneWidget(editor, zoneWidget, lineInfo);
    }
}

interface IMonacoDiscussionThreadControl {
    appendTo($container: JQuery): void;
    setZoneWidget(zoneWidget: DiscussionThreadZoneWidget);
    setSelectedState(selected: boolean, state?: any);
    scrollIntoView();
    updateCommentSizes();
    getThread();
    removeComment(commentId: any);
    getCommentControl(commentId: number);
    updateCommentId(previousCommentId: number, newCommentId: number);
    handleUpdatedThreadId(originalThreadId: number, newThreadId: number);
}

class MonacoDiscussionThreadControl implements IMonacoDiscussionThreadControl {
    public static CSS_CLASS_MENTION_OVERFLOW_VISIBLE = "mention-overflow-visible";

    private _monacoDiscussionThreadManager: DiscussionThreadControlManager;
    private _isOriginalSide: boolean;
    private _widget: DiscussionThreadZoneWidget;
    private _lineDecorationId: string;
    private _position: DiscussionCommon.DiscussionPosition;
    private _thread: DiscussionCommon.DiscussionThread;
    private _$container: JQuery;

    constructor(
        discussionThread: DiscussionCommon.DiscussionThread,
        discussionThreadManager: DiscussionThreadControlManager,
        isOriginalSide: boolean) {
        this._monacoDiscussionThreadManager = discussionThreadManager;
        this._isOriginalSide = isOriginalSide;
        this._position = discussionThread.position;
        this._thread = discussionThread;
    }

    public appendTo($container: JQuery) {
        // Change the containers' style from overflow:hidden to overflow:visible so that the autocomplete menu can be
        //   visible when it extends outside of the comment's bounding box.
        // Monaco editor hard codes the zone widget container as overflow:hidden. Atm, the only way to change the
        //   overflow style is via javascript.
        // An additional problem here is that at this point in time, $container is not yet a child of zone-widget-container
        // Use setTimeout so that the rest of this path that's building the UI can finish and then we find the parent
        // and override the overflow setting
        // Force monaco to not set discussion view zone widgets as aria-hidden
        setTimeout(() => {
            $container.closest(".zone-widget-container").addClass(MonacoDiscussionThreadControl.CSS_CLASS_MENTION_OVERFLOW_VISIBLE);
            $container.closest(".zone-widget").attr("aria-hidden", "false");
        }, 0);

        let $subcontainer = $("<div></div>").prependTo($container);
        this._$container = $subcontainer;
        VSS.using(["VersionControl/Scripts/Components/PullRequestReview/DiscussionThreadHost"], (reactDiscussion: any) => {
            reactDiscussion.DiscussionThreadHost.createDiscussionThread($subcontainer, this._thread, this as any);
        });
    }

    public sizeChanged(lockView: boolean): void {
        if (this._widget) {
            this._widget.handleResize(lockView);
        }
    }

    public hostShouldUnmount() {
        this.dispose();
    }

    public dispose() {
        this.sizeChanged(false);

        VSS.using(["VersionControl/Scripts/Components/PullRequestReview/DiscussionThreadHost"], (reactDiscussion: any) => {
            reactDiscussion.DiscussionThreadHost.removeDiscussionThread(this._$container);

            if (this._thread &&
                this._thread.id < 0 &&
                this._thread.comments &&
                this._thread.comments.length === 0) {
                this._monacoDiscussionThreadManager.getEditor(this._isOriginalSide).focus();
            }
        });

        if (this._widget) {
            this._widget.handleDiscussionContentRemoved(this);
            this._monacoDiscussionThreadManager._releaseWidgetReference(this._widget);
            this._widget = null;
        }

        if (this._lineDecorationId) {
            this._monacoDiscussionThreadManager.getEditor(this._isOriginalSide).deltaDecorations([this._lineDecorationId], []);
            this._lineDecorationId = null;
        }
    }

    public setZoneWidget(zoneWidget: DiscussionThreadZoneWidget) {
        this._widget = zoneWidget;
        zoneWidget.isNewControl = true;
        zoneWidget.appendDiscussionContent(this);

        if (this._position && this._position.positionContext !== DiscussionCommon.PositionContext.InLineBuffer) {
            this._lineDecorationId = this._monacoDiscussionThreadManager.getEditor(this._isOriginalSide).deltaDecorations([], [{
                range: {
                    startLineNumber: this._position.startLine,
                    startColumn: this._position.startColumn,
                    endLineNumber: this._position.endLine,
                    endColumn: this._position.endColumn
                },
                options: {
                    inlineClassName: "commented-text"
                }
            }])[0];
        }
    }

    public setSelectedState(selected: boolean, state?: any) {
        //no such thing as selected anymore?
    }

    public scrollIntoView() {
        if (this._widget) {
            this._widget.scrollIntoView();
        }
    }

    public updateCommentSizes() {
    }

    public focus() {
        this.scrollIntoView();
        //somehow activate the text box so that I can start typing immediately?
    }

    public getThread() {
        return this._thread;
    }

    public removeComment(commentId: any) {
        if (this._widget) {
            this._widget.handleResize();
        }
        return false;
    }

    public getCommentControl(commentId: number) {
        return null;
    }

    public updateCommentId(previousCommentId: number, newCommentId: number) {
    }

    public handleUpdatedThreadId(originalThreadId: number, newThreadId: number) {
    }
}

function isUpwardSelection(selection: monaco.Selection) {
    return selection.positionLineNumber < selection.selectionStartLineNumber ||
        (selection.positionLineNumber === selection.selectionStartLineNumber && selection.positionColumn < selection.selectionStartColumn);
}

function isEmptySelection(selection: monaco.Selection) {
    return !selection || (selection.endLineNumber === selection.startLineNumber && selection.endColumn === selection.startColumn);
}

function isIDiffEditor(editor: monaco.editor.IEditor) {
    return editor.getEditorType() === monaco.editor.EditorType.IDiffEditor;
}

function disposeDisposables(disposables: monaco.IDisposable[]) {
    if (disposables && disposables.length) {
        disposables.forEach((disposable: monaco.IDisposable) => {
            disposable.dispose();
        });
    }
}

function isNullOrUndefined(value: any) {
    return value === undefined || value === null;
}

/**
 * Adjusted user preferences and options to apply across all instances of the Monaco editor and diff.
 */
const preferences: IPreferences = {
    ignoreConfigurationChangedEvent: false,
    editor: {
        lineNumbers: 'on',
        minimap: {},
        wrappingIndent: 'same',
    },
};
